/**
 * Acqs WebSocket live updates
 *
 * Mirror of pm-crm-ws.js — connects to /ws/acqs/ on acqs pages and refreshes
 * list rows / open sheets when records mutate from other tabs/users.
 */

(function () {
    var acqsTabId = (
        window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : String(Date.now()) + '-' + String(Math.random()).slice(2)
    );
    // The two plain `fetch` calls on the offer page (acqs-file-uploader.js,
    // acqs-sow-pdf.js) bypass htmx:configRequest and read the id from here.
    window.acqsTabId = acqsTabId;

    document.addEventListener('htmx:configRequest', function (event) {
        // The offer list (`/acquisitions/`) sends the header without opening the
        // socket, so its next-action edits can show an open offer page the banner.
        if (!isOnAcqsPage() && window.location.pathname !== '/acquisitions/') return;
        event.detail.headers['X-Acqs-Tab-Id'] = acqsTabId;
    });

    // tel: / mailto: links inside row/card surfaces (data-sheet) would otherwise
    // bubble up and open the sheet. Tapping a phone number means "call/text," not "open record."
    document.addEventListener('click', function (event) {
        var link = event.target.closest('a[href^="tel:"], a[href^="mailto:"], a[href^="sms:"]');
        if (link) event.stopPropagation();
    });

    document.body.addEventListener('htmx:responseError', function (event) {
        var xhr = event.detail && event.detail.xhr;
        if (!xhr || xhr.status !== 403 || xhr.responseText.indexOf('Permission denied.') === -1) return;
        if (window.SeeMe && SeeMe.toast) SeeMe.toast.error('Permission denied.');
    });

    // ── Intake-resolution progress ───────────────────────────────────────

    // Resolver broadcasts carry request and parent pursuit ids.  Cards match
    // either identity so corrections can refresh a card with an old request id.
    // A matching event refreshes just the read-only fragment — current progress plus the
    // permission-scoped submission history it wraps — without replacing a sheet
    // or an open correction form.  One state machine per DOM identity serializes
    // bursts; a later event marks the card dirty and reruns against the freshly
    // swapped node after the current request settles.
    var intakeProgressRefreshes = Object.create(null);

    function intakeProgressIdentity(card) {
        if (!card) return null;
        var kind = card.getAttribute('data-intake-kind');
        var recordId = card.getAttribute('data-intake-record-id');
        var surface = card.getAttribute('data-intake-surface');
        if (!kind || !recordId || !surface || !card.id) return null;
        return {id: card.id, kind: kind, recordId: String(recordId), surface: surface};
    }

    function intakeProgressCardStillMatches(card, identity) {
        var current = intakeProgressIdentity(card);
        return !!(
            current && document.body.contains(card)
            && current.id === identity.id
            && current.kind === identity.kind
            && current.recordId === identity.recordId
            && current.surface === identity.surface
        );
    }

    function intakeProgressSignature(card) {
        return (card.getAttribute('data-intake-request-id') || '') + '|' + (card.getAttribute('data-intake-status') || '');
    }

    // `onChanged` runs when the swapped-in card reports a different request or
    // status than the one it replaced. It is dropped when a refresh is already
    // in flight for the card.
    function refreshIntakeProgressCard(card, onChanged) {
        if (!window.htmx) return;
        var identity = intakeProgressIdentity(card);
        if (!identity || !intakeProgressCardStillMatches(card, identity)) return;
        var state = intakeProgressRefreshes[identity.id];
        if (!state) {
            state = intakeProgressRefreshes[identity.id] = {inFlight: false, dirty: false, identity: identity};
        }
        if (state.inFlight) {
            state.dirty = true;
            return;
        }
        var before = intakeProgressSignature(card);
        var url = card.getAttribute('data-refresh-url');
        if (!url) return;
        state.inFlight = true;
        state.dirty = false;
        state.identity = identity;
        var settle = function () {
            state.inFlight = false;
            if (onChanged) {
                var swapped = document.getElementById(identity.id);
                if (intakeProgressCardStillMatches(swapped, identity) && intakeProgressSignature(swapped) !== before) {
                    onChanged(identity);
                }
            }
            if (!state.dirty) return;
            state.dirty = false;
            var currentCard = document.getElementById(identity.id);
            if (intakeProgressCardStillMatches(currentCard, identity)) refreshIntakeProgressCard(currentCard);
        };
        var request = htmx.ajax('GET', url, {
            source: card,
            target: card,
            select: '#' + identity.id,
            selectOOB: '',
            swap: 'outerHTML',
            push: 'false',
        });
        if (request && request.then) {
            request.then(settle, settle);
        } else {
            settle();
        }
    }

    // htmx swaps before its promise resolves.  Refuse a response if navigation
    // or a stacked-sheet change detached/replaced the original surface while it
    // was in flight, so a late fragment cannot paint an unrelated card.
    document.body.addEventListener('htmx:beforeSwap', function (event) {
        var target = event.detail && event.detail.target;
        var identity = intakeProgressIdentity(target);
        var state = identity && intakeProgressRefreshes[identity.id];
        if (state && state.inFlight && !intakeProgressCardStillMatches(target, state.identity)) {
            event.preventDefault();
        }
    });

    // Called with no arguments on reconnect (refresh every card) and with the
    // event's ids otherwise. `requestId` is null for an admission-time review
    // write that has no resolver request yet, so the pursuit ids alone decide.
    // On reconnect a card whose status moved means the socket missed the
    // install's `lead_updated` / `deal_updated`, which is what shows the
    // "Updated elsewhere" banner over the stale Owner and Properties cards, so
    // the banner is shown here instead.
    function refreshLiveIntakeProgressCards(requestId, leadId, dealId) {
        var targeted = !!(requestId || leadId || dealId);
        var onChanged = targeted ? null : function (identity) {
            showStaleBanner(identity.kind, identity.recordId);
        };
        document.querySelectorAll('[data-intake-progress-card]').forEach(function (card) {
            var requestMatches = requestId && String(card.getAttribute('data-intake-request-id')) === String(requestId);
            var recordId = String(card.getAttribute('data-intake-record-id'));
            var kind = card.getAttribute('data-intake-kind');
            var pursuitMatches = (
                (kind === 'deal' && dealId && recordId === String(dealId))
                || (kind !== 'deal' && leadId && recordId === String(leadId))
            );
            if (targeted && !requestMatches && !pursuitMatches) return;
            refreshIntakeProgressCard(card, onChanged);
        });
    }

    // The modern offer page lives at /acquisitions/<pk>/ (plus an optional tab
    // segment, urls/auction_urls.py `name='offer'`), outside /acqs/, so it is
    // matched on its own.
    var OFFER_DETAIL_RE = /^\/acquisitions\/(\d+)\/(?:(?:details|overview|underwriting|tasks|activity|tickets|checklists|pics|files|neighborhood|map)\/)?$/;

    function isOnAcqsPage() {
        return window.location.pathname.indexOf('/acqs/') === 0 || OFFER_DETAIL_RE.test(window.location.pathname);
    }

    // The socket is needed on every /acqs/ page and the offer page, and on any other page (campaign
    // clicks, inbound calls under /marketing/) that hosts a lead or prospect sheet.
    // Both sheets, the full-page lead/prospect/property views and the owner card
    // render `_skiptrace_dropdown.html`, whose root always carries
    // `data-skiptrace-prop` — that is the marker for "a skip trace result can
    // land here". Re-checked after every htmx swap because sheets load via htmx.
    function needsSocket() {
        return isOnAcqsPage() || !!document.querySelector('[data-skiptrace-prop]');
    }

    function isOnPropertyList() {
        return window.location.pathname === '/acqs/properties/';
    }

    function isOnOwnerList() {
        return window.location.pathname === '/acqs/owners/';
    }

    function isOnLeadList() {
        return window.location.pathname === '/acqs/leads/';
    }

    function isOnProspectList() {
        return window.location.pathname === '/acqs/prospects/';
    }

    function isOnSignalQueue() {
        return window.location.pathname === '/acqs/signals/';
    }

    function isOnDealList() {
        return window.location.pathname === '/acqs/deals/';
    }

    function isOnPartnerList() {
        return window.location.pathname === '/acqs/partners/';
    }

    function isOnAssignmentList() {
        return window.location.pathname === '/acqs/assignments/';
    }

    function isOnBuyerList() {
        return window.location.pathname === '/acqs/buyers/';
    }

    function isOnListingList() {
        return window.location.pathname === '/acqs/listings/';
    }

    function isOnLeadDetail(leadId) {
        // matches /acqs/leads/<id>/ or /acqs/leads/<id>/anything
        var m = window.location.pathname.match(/^\/acqs\/leads\/(\d+)\//);
        return !!(m && parseInt(m[1], 10) === leadId);
    }

    function isOnProspectDetail(prospectId) {
        var m = window.location.pathname.match(/^\/acqs\/prospects\/(\d+)\//);
        return !!(m && parseInt(m[1], 10) === prospectId);
    }

    function isOnListingDetail(listingId) {
        var m = window.location.pathname.match(/^\/acqs\/listings\/(\d+)\/page\//);
        return !!(m && parseInt(m[1], 10) === listingId);
    }

    function isOnDealDetail(dealId) {
        var m = window.location.pathname.match(/^\/acqs\/deals\/(\d+)\//);
        return !!(m && parseInt(m[1], 10) === dealId);
    }

    function isOnOfferDetail(offerId) {
        var m = window.location.pathname.match(OFFER_DETAIL_RE);
        return !!(m && parseInt(m[1], 10) === offerId);
    }

    // ── Row helpers ────────────────────────────────────────────────────────

    // The list page's query string carries the active ?stage= tab + filters.
    // Forwarding it lets the row/tabs endpoints scope correctly — and lets the
    // row endpoint 404 (→ removeRow) a row that no longer matches the tab.
    function listQuery() {
        return window.location.search || '';
    }

    // `whenReady` (optional) hands the caller control of WHEN the fetched markup
    // is applied — it receives the apply function and decides. Deal rows use it
    // to hold the swap while the rep has a dropdown open or an input focused in
    // that row. The check has to run here and not only before the request,
    // because the fetch is async: a dropdown can be opened while it is in
    // flight. The element is re-read by id inside apply() rather than reusing
    // the captured `row`, since a deferred apply can run long after an htmx OOB
    // swap replaced the node.
    function refreshRow(elementId, url, whenReady) {
        var row = document.getElementById(elementId);
        if (!row) return;
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('HX-Request', 'true');
        // Forward current URL so server-side helpers (e.g. _is_mobile) can detect
        // force_mobile=1 — matches what htmx would send for a real swap.
        xhr.setRequestHeader('HX-Current-URL', window.location.href);
        xhr.onload = function () {
            var apply;
            if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim()) {
                apply = function () {
                    var live = document.getElementById(elementId);
                    if (!live) return;
                    var parent = live.parentNode;
                    if (!parent) return;
                    live.outerHTML = xhr.responseText;
                    if (window.htmx) htmx.process(parent);
                };
            } else if (xhr.status === 404) {
                apply = function () { removeRow(document.getElementById(elementId)); };
            } else {
                return;
            }
            if (whenReady) whenReady(apply); else apply();
        };
        xhr.send();
    }

    // A card that can render on a detail page and inside a sheet stacked over it
    // carries a `-sheet` suffix on the sheet's copy (the server side of this lives
    // in acqs/views/helpers.py, SURFACE_SUFFIX). A broadcast has no idea which
    // surface this viewer has open, and `getElementById` would hand back whichever
    // copy comes first in the document — so resolve every copy and let the caller
    // pick by its own data attributes. With no suffix in play this returns the one
    // card and behaves exactly as before.
    function surfaceCards(baseId) {
        return document.querySelectorAll('#' + baseId + ', #' + baseId + '-sheet');
    }

    // `target` is an element id, or a function returning the live element for
    // callers that must not re-resolve by id (an id shared by a page card and
    // its sheet copy, where getElementById always returns the page's). It is
    // called again when the response lands, so a node replaced in the meantime
    // is never written to. `callback(next, prev)` gets the new element and the
    // detached old one, which still holds its pre-swap UI state. `prepare(html)`
    // handles what else the response carries and returns the one root to swap.
    function refreshFragment(target, url, callback, prepare) {
        var resolve = typeof target === 'function'
            ? target
            : function () { return document.getElementById(target); };
        var el = resolve();
        if (!el) return false;
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.setRequestHeader('HX-Current-URL', window.location.href);
        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim()) {
                el = resolve();
                if (!el) return;
                var next = swapFragment(el, prepare ? prepare(xhr.responseText) : xhr.responseText);
                if (next && callback) callback(next, el);
            }
        };
        xhr.send();
        return true;
    }

    // Replace `el` with `html` (one root element) and wire the new node up as an
    // htmx swap would. Returns the new element, or null when `el` was detached.
    function swapFragment(el, html) {
        var parent = el.parentNode;
        if (!parent) return null;
        var before = el.previousSibling;
        // A tabbed card (the property details card) always renders with
        // its first tab active, so a broadcast refresh while the viewer
        // is on Data / Comps / Map would bounce them back to Details.
        // Remember the active tab and put it back on the new markup.
        var activeTabBtn = el.querySelector('.tabs .tabs-button--active');
        var activeTab = activeTabBtn && activeTabBtn.getAttribute('data-tab');
        el.outerHTML = html;
        var next = before ? before.nextElementSibling : parent.firstElementChild;
        if (window.htmx && next) htmx.process(next);
        if (next && activeTab) {
            var tabBtn = next.querySelector('.tabs [data-tab="' + activeTab + '"]');
            if (tabBtn && !tabBtn.classList.contains('tabs-button--active')) tabBtn.click();
        }
        // htmx.process() does NOT fire htmx:afterSettle, so SeeMe's
        // clamp.js never re-applies the "show more" truncation to the
        // swapped-in node — the card would come back fully expanded.
        // Re-init the list clamp the same way sheets.js does after an
        // innerHTML body swap (static/js/src/sheets.js).
        if (next && window._cc && window._cc.applyClampList) {
            next.querySelectorAll('[data-clamp-list]').forEach(window._cc.applyClampList);
        }
        // Same reason: browser-local-datetime.js listens on HTMX/sheet
        // lifecycle events only, so without this the swapped-in
        // <time data-browser-local-datetime> keeps the server fallback
        // zone instead of the viewer's own.
        if (next && window._cc && window._cc.applyBrowserLocalDatetime) {
            window._cc.applyBrowserLocalDatetime(next);
        }
        // Same reason: tabs-collapse.js re-measures only on settle, sheet:ready
        // and resize, so a swapped-in strip (the conversation's line tabs) would
        // come back unfolded until the next resize. Lay it out directly.
        if (next && window._cc && window._cc.updateTabsCollapse) window._cc.updateTabsCollapse(next);
        return next;
    }

    function removeRow(row) {
        if (!row) return;
        var container = row.closest('tbody') || row.closest('.list-group');
        row.remove();
        if (!container) return;
        var isMobile = container.classList.contains('list-group');
        var isEmpty = isMobile
            ? !container.querySelector('.list-group-item')
            : !container.querySelector('tr');
        if (isEmpty) {
            if (isMobile) {
                container.insertAdjacentHTML('beforeend',
                    '<div class="data-table__empty"><i class="fa-solid fa-inbox"></i> No results found</div>');
            } else {
                var colCount = container.closest('table').querySelectorAll('thead th').length || 4;
                container.insertAdjacentHTML('beforeend',
                    '<tr><td colspan="' + colCount + '" class="data-table__empty">No results found</td></tr>');
            }
        }
    }

    // A list body with no matching rows renders an empty-state placeholder in
    // its place ({% empty %} in every _*_results.html), so a row inserted into
    // that body has to clear the placeholder or the viewer sees the new row and
    // "No deals found" together. This is the inverse of what removeRow builds
    // when the last row leaves, and it reads the same marker: the placeholder is
    // a <tr> whose only <td> carries .data-table__empty in a tbody, and a
    // .data-table__empty div directly inside the container on the mobile
    // .list-group. Dropping the <td>'s row (or the div itself) leaves the body
    // in the state a server-side render of the same list would produce.
    function clearEmptyState(container) {
        var placeholder = container.querySelector('.data-table__empty');
        if (!placeholder) return;
        (placeholder.closest('tr') || placeholder).remove();
    }

    // Insert-or-replace: if the row already exists (e.g. the OOB swap from the
    // creating tab beat the WS event), refresh in place; otherwise fetch and
    // prepend to the tbody.
    //
    // `whenReady` (optional) is forwarded to refreshRow on the refresh branch —
    // see refreshRow. The insert branch deliberately does NOT take it: a row
    // that is not on screen cannot be the row the rep is typing in, so there is
    // nothing to defer, and holding an insert would only delay it.
    function upsertRow(elementId, tbodyId, url, whenReady) {
        if (document.getElementById(elementId)) {
            refreshRow(elementId, url, whenReady);
            return;
        }
        var tbody = document.getElementById(tbodyId);
        if (!tbody) return;
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.setRequestHeader('HX-Current-URL', window.location.href);
        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim()) {
                clearEmptyState(tbody);
                tbody.insertAdjacentHTML('afterbegin', xhr.responseText);
                var inserted = document.getElementById(elementId);
                if (window.htmx && inserted) htmx.process(inserted);
            }
        };
        xhr.send();
    }

    // ── Defer a swap while the rep is editing that row ─────────────────────

    // A Deal Flow row is not read-only: it carries a Headline textarea and a
    // Mark Passed/Missed dropdown-form with outcome radios, a reason select and
    // free-text commentary. Replacing the <tr> while someone is typing destroys
    // the text with no warning, so a row that is being edited holds its refresh
    // (or removal) until it is free again.
    //
    // "Being edited" is two conditions:
    //   1. An open dropdown owned by the row. dropdowns.js portals an open menu
    //      to <body> (every row menu qualifies — the trigger sits inside
    //      .data-table), so `.dropdown-menu.show` is NOT inside the row; what
    //      stays behind is the `.dropdown` wrapper, and every open path
    //      (in-place, popout, sheet) sets `.show` on it. That is the marker.
    //   2. document.activeElement inside the row, narrowed to real text entry —
    //      covers a focused input/textarea outside a dropdown. Chromium focuses
    //      a clicked button, so a blanket contains(activeElement) test would
    //      hold the row indefinitely after any button click in it; dropdowns.js
    //      narrows its own equivalent guard the same way.
    //
    // At most one action is held per row, so a burst of events on the same row
    // collapses into a single swap of the newest markup instead of queueing
    // several. The interval is the catch-all wake-up (Escape-to-close and
    // click-outside close a dropdown without any event this module listens
    // for); focusout and click just make the common cases feel immediate.
    var ROW_SWAP_RETRY_MS = 400;
    var pendingRowSwaps = {};
    var pendingRowSwapTimer = null;

    function isTextEntry(el) {
        if (!el || !el.matches) return false;
        if (el.isContentEditable) return true;
        if (!el.matches('input, textarea, select')) return false;
        return !el.matches('input[type=checkbox], input[type=radio], input[type=button], input[type=submit], input[type=reset]');
    }

    function rowIsBusy(row) {
        if (!row) return false;
        // `matches` as well as `querySelector`: dropdowns.js puts `.show` on the
        // `.dropdown` ROOT, and `_property_attom_dropdown.html` IS that root — it
        // carries the `data-refresh-url` itself. A descendant-only test can never see
        // its own open menu, so the busy guard was inert for the one element whose
        // menu stays open across the click (`data-dropdown-keep-open` on the enrich
        // buttons). List rows are never `.dropdown` themselves, so this only ever adds
        // coverage for the card case.
        if (row.matches('.dropdown.show') || row.querySelector('.dropdown.show')) return true;
        var active = document.activeElement;
        return !!(active && isTextEntry(active) && row.contains(active));
    }

    function runPendingRowSwaps() {
        Object.keys(pendingRowSwaps).forEach(function (key) {
            var pending = pendingRowSwaps[key];
            if (rowIsBusy(pending.resolve())) return;
            delete pendingRowSwaps[key];
            pending.action();
        });
        if (pendingRowSwapTimer && !Object.keys(pendingRowSwaps).length) {
            clearInterval(pendingRowSwapTimer);
            pendingRowSwapTimer = null;
        }
    }

    // `key` is the element id, unless `resolve` is given: then `key` only names
    // the pending slot and `resolve()` returns the live element to test. Cards
    // whose id repeats across the page and a sheet use that form, since
    // getElementById would test the page copy for both.
    function deferWhileEditing(key, action, resolve) {
        resolve = resolve || function () { return document.getElementById(key); };
        if (!rowIsBusy(resolve())) {
            action();
            return;
        }
        pendingRowSwaps[key] = {action: action, resolve: resolve};
        if (!pendingRowSwapTimer) {
            pendingRowSwapTimer = setInterval(runPendingRowSwaps, ROW_SWAP_RETRY_MS);
        }
    }

    function checkPendingRowSwapsSoon() {
        if (!pendingRowSwapTimer) return;
        // focusout fires before focus lands on the next element, and a click
        // that closes a dropdown is only reflected after dropdowns.js's own
        // document listener has run — re-check on the next tick either way.
        setTimeout(runPendingRowSwaps, 0);
    }

    document.addEventListener('focusout', checkPendingRowSwapsSoon);
    document.addEventListener('click', checkPendingRowSwapsSoon);

    // ── Property ───────────────────────────────────────────────────────────

    function refreshPropertyRow(propertyId) {
        refreshRow('property-' + propertyId, '/acqs/properties/' + propertyId + '/row/');
    }

    function upsertPropertyRow(propertyId) {
        upsertRow(
            'property-' + propertyId,
            'property-list-tbody',
            '/acqs/properties/' + propertyId + '/row/'
        );
    }

    function removePropertyRow(propertyId) {
        removeRow(document.getElementById('property-' + propertyId));
    }

    // ── Owner ──────────────────────────────────────────────────────────────

    function refreshOwnerRow(ownerId) {
        refreshRow('owner-' + ownerId, '/acqs/owners/' + ownerId + '/row/');
    }

    function upsertOwnerRow(ownerId) {
        upsertRow(
            'owner-' + ownerId,
            'owner-list-tbody',
            '/acqs/owners/' + ownerId + '/row/'
        );
    }

    function removeOwnerRow(ownerId) {
        removeRow(document.getElementById('owner-' + ownerId));
    }

    // ── Buyer ──────────────────────────────────────────────────────────────

    // Rows are keyed by buyer id (`buyer-<pk>`), refreshed on `buyer_updated`.
    // A buyer can have owners or none; each row lists its owner ids in
    // `data-buyer-owners`, so `owner_updated` refreshes the row of that owner's
    // buyer. Refresh, never upsert: the row endpoint applies only the `?status=`
    // tab, so a row that left the tab 404s and is removed (see the websockets
    // doc). The row's headline dropdown holds a textarea, so the swap waits while
    // the rep has a dropdown open or is typing in the row, like a deal row.
    function refreshBuyerRow(buyerId) {
        var elementId = 'buyer-' + buyerId;
        refreshRow(elementId, '/acqs/buyers/' + buyerId + '/row/' + listQuery(), function (apply) {
            deferWhileEditing(elementId, apply);
        });
    }

    function refreshBuyerRowsForOwner(ownerId) {
        document.querySelectorAll('tr[data-buyer-owners~="' + ownerId + '"]').forEach(function (row) {
            refreshBuyerRow(row.id.slice('buyer-'.length));
        });
    }

    // The buyer page and sheet: on `buyer_updated`, every copy of that buyer on
    // screen re-fetches its facts card, Activity card and header Revoked owner
    // badge, plus on a link change its Owners chip, Contacts block placeholder and
    // Properties card (sheet only; the page has no copy), from
    // `acqs:buyer-sections`, in one request per copy. The facts card marks a
    // copy (`data-buyer-sections`, `data-refresh-url` naming its surface), so
    // a sheet over the Buyers list, over another buyer's page or over this
    // buyer's page is found by DOM, not URL. Each section in the
    // response swaps the element with its own id; the ids carry the buyer and a
    // `-sheet` suffix, so they are unique in the document. The swap waits while
    // the rep has the Owners chip's menu open or is editing in the Contacts
    // block, the two sections with controls in them.
    var BUYER_SECTION_IDS = [
        'buyer-owner-chip-{id}{sfx}', 'buyer-facts-{id}{sfx}',
        'contacts-block-{surface}-buyer-{id}', 'buyer-activity-card-{id}{sfx}',
        'buyer-revoked-badge-{id}{sfx}', 'buyer-{id}-properties{sfx}',
    ];

    // `links` (the broadcast's flag for an owner or contact link change) is
    // the only time the Owners chip, Contacts block and Properties card come
    // along; a field or status edit re-fetches the facts card, Activity card
    // and badge only.
    function refreshBuyerSections(buyerId, links) {
        var id = String(buyerId).replace(/[^0-9]/g, '');
        if (!id) return;
        document.querySelectorAll('[data-buyer-sections="' + id + '"]').forEach(function (marker) {
            var url = marker.getAttribute('data-refresh-url');
            if (!url) return;
            var surface = new URLSearchParams(url.split('?')[1] || '').get('surface') === 'sheet' ? 'sheet' : 'page';
            var ids = BUYER_SECTION_IDS.map(function (tpl) {
                return tpl.replace('{id}', id).replace('{sfx}', surface === 'sheet' ? '-sheet' : '')
                    .replace('{surface}', surface);
            });
            var byId = function (elementId) {
                return function () { return document.getElementById(elementId); };
            };
            if (!links) {
                fetchBuyerSections(url, [ids[1], ids[3], ids[4]]);
                return;
            }
            // Chip first, then the Contacts block; each slot holds until its own
            // section is free.
            deferWhileEditing(ids[0], function () {
                deferWhileEditing(ids[2], function () {
                    fetchBuyerSections(url, ids);
                }, byId(ids[2]));
            }, byId(ids[0]));
        });
    }

    function fetchBuyerSections(url, ids) {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.setRequestHeader('HX-Current-URL', window.location.href);
        xhr.onload = function () {
            if (xhr.status < 200 || xhr.status >= 300) return;
            var tmp = document.createElement('template');
            tmp.innerHTML = xhr.responseText;
            ids.forEach(function (elementId) {
                var html = tmp.content.getElementById(elementId);
                var live = document.getElementById(elementId);
                if (!html || !live) return;
                var next = swapFragment(live, html.outerHTML);
                if (elementId.indexOf('buyer-activity-card-') === 0) restoreActivityCardView(next, live);
            });
        };
        xhr.send();
    }

    // ── Lead ───────────────────────────────────────────────────────────────

    function refreshLeadRow(leadId) {
        refreshRow('lead-' + leadId, '/acqs/leads/' + leadId + '/row/' + listQuery());
    }

    function upsertLeadRow(leadId) {
        upsertRow(
            'lead-' + leadId,
            'lead-list-tbody',
            '/acqs/leads/' + leadId + '/row/' + listQuery()
        );
    }

    function removeLeadRow(leadId) {
        removeRow(document.getElementById('lead-' + leadId));
    }

    // ── Prospect ───────────────────────────────────────────────────────────

    function refreshProspectRow(prospectId) {
        refreshRow('prospect-' + prospectId, '/acqs/prospects/' + prospectId + '/row/' + listQuery());
    }

    function upsertProspectRow(prospectId) {
        upsertRow(
            'prospect-' + prospectId,
            'prospect-list-tbody',
            '/acqs/prospects/' + prospectId + '/row/' + listQuery()
        );
    }

    function removeProspectRow(prospectId) {
        removeRow(document.getElementById('prospect-' + prospectId));
    }

    // ── Deal ───────────────────────────────────────────────────────────────

    // Only the APPLY is deferred, never the fetch. `pendingRowSwaps` is a single
    // last-write-wins slot per row, so deferring both against the same key made
    // an arriving apply overwrite a not-yet-issued fetch: event #1 fetches while
    // the row is idle, the rep opens a dropdown, event #2 parks its fetch in the
    // slot, then response #1 lands and parks its apply there instead — event #2
    // never fetched, and the row was eventually painted with markup read before
    // event #2's mutation. The fetch is a cheap GET that is safe to run while the
    // rep is editing, so the newest event always wins the slot.
    function refreshDealRow(dealId) {
        var elementId = 'deal-' + dealId;
        var url = '/acqs/deals/' + dealId + '/row/' + listQuery();
        refreshRow(elementId, url, function (apply) {
            deferWhileEditing(elementId, apply);
        });
    }

    // Both deal_created and deal_updated come through here. An update has to
    // upsert, not refresh: a deal whose stage changes INTO the tab the viewer is
    // filtered on has no row to refresh, and refreshRow returns immediately when
    // the element is absent — the stage count moved while the list still read
    // "No deals found". The editing guard rides along on the refresh branch, so
    // an update to a row already on screen is still held while the rep types.
    function upsertDealRow(dealId) {
        var elementId = 'deal-' + dealId;
        var url = '/acqs/deals/' + dealId + '/row/' + listQuery();
        upsertRow(elementId, 'deal-list-tbody', url, function (apply) {
            deferWhileEditing(elementId, apply);
        });
    }

    // There is deliberately no removeDealRow: nothing deletes or archives an
    // AcqDeal, so there is no deal_deleted event. A deal that leaves the active
    // tab (passed, missed, stage change) is removed by the refresh branch — the
    // row endpoint 404s and refreshRow's 404 path drops the row, through the
    // same editing guard as an ordinary swap.

    // Not refreshFragment (which the lead/prospect tab refreshes use): the deal
    // endpoint returns #tabs-bar PLUS the two filter-dropdown chip lists as
    // hx-swap-oob siblings, and an outerHTML assignment ignores OOB markup
    // entirely, so the counts inside #filter-bar would never move. htmx.ajax
    // runs the OOB pass before applying `select`, so both chip lists land and
    // #tabs-bar is still the primary swap.
    //
    // `select` and `push` are passed explicitly because htmx falls back to the
    // source element's own attributes: #tabs-bar carries hx-select="#page-content"
    // (which no fragment contains, so the primary swap would delete the tab bar)
    // and hx-push-url="true" (which would push /acqs/deals/tabs/ into history).
    // `selectOOB` is deliberately NOT passed and no ancestor of #tabs-bar
    // defines hx-select-oob — re-selecting #filter-bar would discard whatever
    // the rep is typing in the address search box.
    //
    // Bursts are serialised through an in-flight + dirty pair rather than a
    // debounce, because a debounce still loses an event that arrives after the
    // quiet window but inside one slow round-trip. htmx cannot serialise this
    // itself: it keys its request state on the source element, and `swap:
    // outerHTML` detaches that element. A second htmx.ajax issued mid-flight is
    // QUEUED against the old node (htmx.js:4373-4388, default `queue last`), and
    // the queue is only drained by endRequestLock() in xhr.onload's finally
    // (htmx.js:4602) — i.e. after the swap already detached the node. The
    // re-issued request then hits `if (!bodyContains(elt)) return`
    // (htmx.js:4284) and dies silently: no request, no swap, no OOB pass, no
    // console error, and the tab counts and both chip lists keep their pre-burst
    // numbers until the next deal event. Holding the second refresh here instead
    // means #tabs-bar is re-resolved by id after the swap, so the follow-up runs
    // against the freshly attached node.
    //
    // htmx.ajax returns a promise (`returnPromise: true`, htmx.js:4064/4081)
    // that resolves in xhr.onload after the swap (htmx.js:4597) and rejects on
    // send error / abort / timeout (htmx.js:4609, :4616, :4623), so both
    // settlement paths clear the flag and neither strands a pending refresh.
    var dealTabsRefreshInFlight = false;
    var dealTabsRefreshDirty = false;

    function refreshDealTabs() {
        if (!window.htmx) return;
        if (dealTabsRefreshInFlight) {
            dealTabsRefreshDirty = true;
            return;
        }
        // Everything requested up to here is covered by the run below — or by
        // nothing at all, if the tab bar has since left the page.
        dealTabsRefreshDirty = false;
        var tabs = document.getElementById('tabs-bar');
        if (!tabs) return;
        dealTabsRefreshInFlight = true;
        var settled = function () {
            dealTabsRefreshInFlight = false;
            if (dealTabsRefreshDirty) refreshDealTabs();
        };
        var request = htmx.ajax('GET', '/acqs/deals/tabs/' + listQuery(), {
            source: tabs,
            target: tabs,
            select: '#tabs-bar',
            swap: 'outerHTML',
            push: 'false',
        });
        if (request && request.then) {
            request.then(settled, settled);
        } else {
            settled();
        }
    }

    // ── Assignment ─────────────────────────────────────────────────────────

    // Upsert, as for deals: `assignment_row_partial` re-applies the list's tab
    // and every filter, so a 200 means the row matches this viewer's tab and
    // filters (a create, or a change into the filtered invoice status, inserts
    // it) and a 404 drops it (Cancel or Close moving it off Active). The row
    // does not check sort or page: a new row goes to the top of the page the
    // viewer has open and the "Showing" count stays stale until the next
    // reload, as on Deal Flow. The rows hold no
    // inputs or dropdowns — only the address and buyer links open sheets — so there is no
    // editing guard to pass.
    function upsertAssignmentRow(assignmentId) {
        upsertRow(
            'assignment-' + assignmentId,
            'assignment-list-tbody',
            '/acqs/assignments/' + assignmentId + '/row/' + listQuery()
        );
    }

    // The refreshDealTabs shape — see the comment above it for why the refresh
    // goes through htmx.ajax with an in-flight + dirty pair. `selectOOB` must be
    // truthy to override anything: htmx reads '' as "not given" and falls back
    // to #tabs-bar's own hx-select-oob="#filter-bar, #tabs-bar"
    // (_assignment_tabs.html). That would OOB-swap #tabs-bar out of the response
    // before the primary `select` ran, leaving the primary swap an empty fragment
    // against a node the OOB pass already replaced. 'unset' names no element in
    // the tabs response, so the OOB pass does nothing and #filter-bar, with any
    // text being typed in its search box, is never touched.
    var assignmentTabsRefreshInFlight = false;
    var assignmentTabsRefreshDirty = false;

    function refreshAssignmentTabs() {
        if (!window.htmx) return;
        if (assignmentTabsRefreshInFlight) {
            assignmentTabsRefreshDirty = true;
            return;
        }
        assignmentTabsRefreshDirty = false;
        var tabs = document.getElementById('tabs-bar');
        if (!tabs) return;
        assignmentTabsRefreshInFlight = true;
        var settled = function () {
            assignmentTabsRefreshInFlight = false;
            if (assignmentTabsRefreshDirty) refreshAssignmentTabs();
        };
        var request = htmx.ajax('GET', '/acqs/assignments/tabs/' + listQuery(), {
            source: tabs,
            target: tabs,
            select: '#tabs-bar',
            selectOOB: 'unset',
            swap: 'outerHTML',
            push: 'false',
        });
        if (request && request.then) {
            request.then(settled, settled);
        } else {
            settled();
        }
    }

    // ── Seller listing ─────────────────────────────────────────────────────

    // The assignment shape: `listing_row_partial` re-applies the list's tab and
    // filters, so a 200 inserts or refreshes the row and a 404 drops it (a
    // cancel or close moving it off Open). The rows hold no inputs, so there
    // is no editing guard to pass.
    function upsertListingRow(listingId) {
        upsertRow(
            'listing-' + listingId,
            'listing-list-tbody',
            '/acqs/listings/' + listingId + '/row/' + listQuery()
        );
    }

    // An open listing page or sheet: re-fetch both next-action summaries (page
    // rail and sheet) through their data-refresh-url, held while their dropdown
    // is open.
    function refreshListingNextActionSummaries(listingId) {
        var id = String(listingId).replace(/[^0-9]/g, '');
        if (!id) return;
        refreshByRefreshUrl('listing-next-action-summary-' + id);
        refreshByRefreshUrl('listing-next-action-summary-' + id + '-page');
    }

    // refreshAssignmentTabs, for `_listing_tabs.html` — see the comments above
    // it for the in-flight + dirty pair and why `selectOOB` is 'unset'.
    var listingTabsRefreshInFlight = false;
    var listingTabsRefreshDirty = false;

    function refreshListingTabs() {
        if (!window.htmx) return;
        if (listingTabsRefreshInFlight) {
            listingTabsRefreshDirty = true;
            return;
        }
        listingTabsRefreshDirty = false;
        var tabs = document.getElementById('tabs-bar');
        if (!tabs) return;
        listingTabsRefreshInFlight = true;
        var settled = function () {
            listingTabsRefreshInFlight = false;
            if (listingTabsRefreshDirty) refreshListingTabs();
        };
        var request = htmx.ajax('GET', '/acqs/listings/tabs/' + listQuery(), {
            source: tabs,
            target: tabs,
            select: '#tabs-bar',
            selectOOB: 'unset',
            swap: 'outerHTML',
            push: 'false',
        });
        if (request && request.then) {
            request.then(settled, settled);
        } else {
            settled();
        }
    }

    // ── Bot inbox ────────────────────────────────────────────────────

    // The page's one refresh path: a socket event and a rep's own action in this tab both land
    // here. Row membership moves between tabs and the tab counts sit in the wrapper, so the whole
    // wrapper re-fetches, with the debounce and in-flight + dirty pair the partner roster's
    // comment below explains. The tab and page come from the current URL.
    var SELLER_BOT_REFRESH_DEBOUNCE_MS = 300;
    var sellerBotRefreshTimer = null;
    var sellerBotRefreshInFlight = false;
    var sellerBotRefreshDirty = false;

    function isOnSellerBotPage() {
        return window.location.pathname === '/acqs/seller-bot/';
    }

    function refreshSellerBotSoon() {
        if (!isOnSellerBotPage()) return;
        if (sellerBotRefreshTimer) clearTimeout(sellerBotRefreshTimer);
        sellerBotRefreshTimer = setTimeout(function () {
            sellerBotRefreshTimer = null;
            refreshSellerBot();
        }, SELLER_BOT_REFRESH_DEBOUNCE_MS);
    }

    function refreshSellerBot() {
        if (!isOnSellerBotPage() || !window.htmx) return;
        if (sellerBotRefreshInFlight) {
            sellerBotRefreshDirty = true;
            return;
        }
        sellerBotRefreshDirty = false;
        var wrapper = document.getElementById('list-results-wrapper');
        if (!wrapper) return;
        sellerBotRefreshInFlight = true;
        var settled = function () {
            sellerBotRefreshInFlight = false;
            if (sellerBotRefreshDirty) refreshSellerBot();
        };
        var request = htmx.ajax('GET', window.location.href, {
            source: wrapper,
            target: wrapper,
            select: '#list-results-wrapper',
            swap: 'outerHTML',
            push: 'false',
        });
        if (request && request.then) {
            request.then(settled, settled);
        } else {
            settled();
        }
    }

    function refreshSellerBotForLead(leadId) {
        if (isOnSellerBotPage() && document.querySelector('tr[data-lead-id="' + leadId + '"]')) {
            refreshSellerBotSoon();
        }
    }

    document.body.addEventListener('acqsBotRunChanged', refreshSellerBotSoon);
    document.body.addEventListener('acqsBotDraftChanged', refreshSellerBotSoon);

    // ── Seller bot drafts on an open sheet ─────────────────────────────────
    // A new, discarded or newly held draft re-fetches the sheet's slot (acqs/_bot_drafts.html). A
    // card this rep is editing keeps the slot as it is, both when the event lands and when the
    // response does; Cancel fires acqsBotDraftChanged, which catches up. A slot showing only
    // resolved cards stays when nothing replaces them, so a rep's own Reject or Approve keeps its
    // result on screen, except on a lead's SMS sheet (`data-bot-drafts-moved`), whose timeline shows it.
    var BOT_DRAFTS_REFRESH_DEBOUNCE_MS = 300;
    var botDraftsRefreshTimer = null;

    function botDraftsEditing(slot) {
        return !!slot.querySelector('[data-bot-editing]');
    }

    function refreshBotDraftSlot(slot) {
        if (botDraftsEditing(slot)) return;
        var xhr = new XMLHttpRequest();
        xhr.open('GET', slot.dataset.refreshUrl);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.onload = function () {
            if (xhr.status !== 200 || !slot.isConnected || botDraftsEditing(slot)) return;
            var next = new DOMParser().parseFromString(xhr.responseText, 'text/html');
            var cards = slot.querySelectorAll('[data-bot-card-status]');
            var resolved = slot.querySelectorAll('[data-bot-card-resolved]');
            var moved = slot.hasAttribute('data-bot-drafts-moved');
            if (!moved && cards.length && cards.length === resolved.length && !next.querySelector('[data-bot-card-status]')) return;
            // On a lead's SMS sheet the slot is the thread's end, so a reader at the end stays there.
            var container = moved ? scrollContainerOf(slot) : null;
            var follow = container && isNearBottom(container);
            swapFragment(slot, xhr.responseText);
            if (follow) container.scrollTop = container.scrollHeight;
        };
        xhr.send();
    }

    function refreshBotDrafts(leadId) {
        if (botDraftsRefreshTimer) clearTimeout(botDraftsRefreshTimer);
        botDraftsRefreshTimer = setTimeout(function () {
            botDraftsRefreshTimer = null;
            var selector = leadId ? '[data-bot-drafts][data-lead-id="' + leadId + '"]' : '[data-bot-drafts]';
            document.querySelectorAll(selector).forEach(refreshBotDraftSlot);
            // An Emails list's rows say which thread holds a reply draft; a sent draft is a new message.
            var pkMatch = '[data-email-kind="lead"]' + (leadId ? '[data-email-pk="' + leadId + '"]' : '');
            document.querySelectorAll('[data-email-list]' + pkMatch + ', [data-email-thread-messages]' + pkMatch).forEach(function (el) {
                refreshTargetEmails('lead', el.getAttribute('data-email-pk'));
            });
            // A toolbar's icon for the other channel: its dot says that channel has a draft.
            var icons = leadId ? '[data-bot-channel-icon][data-lead-id="' + leadId + '"]' : '[data-bot-channel-icon]';
            document.querySelectorAll(icons).forEach(function (icon) {
                var xhr = new XMLHttpRequest();
                xhr.open('GET', icon.dataset.refreshUrl);
                xhr.setRequestHeader('HX-Request', 'true');
                xhr.onload = function () {
                    if (xhr.status === 200 && icon.isConnected) swapFragment(icon, xhr.responseText);
                };
                xhr.send();
            });
        }, BOT_DRAFTS_REFRESH_DEBOUNCE_MS);
    }

    document.body.addEventListener('acqsBotDraftChanged', function () {
        refreshBotDrafts();
        refreshBotComposer();
    });

    // A rep's instruction replaces these cards, an open editor of theirs too, so the slot may swap.
    document.body.addEventListener('acqsBotInstructionSent', function (e) {
        ((e.detail && e.detail.cards) || []).forEach(function (pk) {
            var card = document.getElementById('eval-event-' + pk);
            var editing = card && card.querySelector('[data-bot-editing]');
            if (editing) editing.removeAttribute('data-bot-editing');
        });
        refreshBotDrafts();
        refreshBotComposer();
    });

    // ── The line and mode picker in a composer (acqs/_bot_composer_slot.html) ──
    // Each refresh swaps those in place, never the message, and keeps the rep's mode.  Message Seller is the
    // default, so only Instruction to Bot, which the rep picked, is passed back.  When it goes away in instruction
    // mode, the send would become a text to the seller, so their note is cleared and they are told.  An open menu
    // holds the swap until it closes.
    function refreshBotComposerBlock(block) {
        var form = block.closest('form');
        var sendAs = form && form.querySelector('input[name="send_as"]');
        var mode = sendAs ? sendAs.value : 'message';
        var message = form && form.querySelector('textarea[name="message"]');
        var xhr = new XMLHttpRequest();
        xhr.open('GET', block.dataset.refreshUrl + (mode === 'instruction' ? '&send_as=instruction' : ''));
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.onload = function () {
            if (xhr.status !== 200) return;
            var tpl = document.createElement('template');
            tpl.innerHTML = xhr.responseText;
            var nextBlock = tpl.content.querySelector('[data-bot-composer]');
            var nextPicker = tpl.content.querySelector('[data-bot-mode-picker]');
            var currentPicker = function () { return form ? form.querySelector('[data-bot-mode-picker]') : null; };
            deferWhileEditing('bot-mode-picker-' + block.dataset.leadId, function () {
                if (!block.isConnected || !nextBlock) return;
                var picker = currentPicker();
                if (picker && nextPicker) swapFragment(picker, nextPicker.outerHTML);
                swapFragment(block, nextBlock.outerHTML);
                if (!form) return;
                var live = form.querySelector('input[name="send_as"]');
                if (mode === 'instruction' && live && live.value !== 'instruction' && message && message.value.trim()) {
                    message.value = '';
                    message.dispatchEvent(new Event('input', { bubbles: true }));
                    if (window.SeeMe && SeeMe.toast) {
                        SeeMe.toast.warning('Instruction to Bot is off for this lead now, so your note was cleared.');
                    }
                }
                if (window.acqsBotComposerSync) window.acqsBotComposerSync(form);
            }, currentPicker);
        };
        xhr.send();
    }

    function refreshBotComposer(leadId) {
        var selector = leadId ? '[data-bot-composer][data-lead-id="' + leadId + '"]' : '[data-bot-composer]';
        document.querySelectorAll(selector).forEach(refreshBotComposerBlock);
    }

    document.body.addEventListener('acqsBotRunChanged', function () { refreshBotComposer(); });

    // ── Partner roster ─────────────────────────────────────────────────────

    // Partner rows render partner.deal_count, so any deal create or edit can
    // move a number on this list. The broadcast carries no partner id, so a
    // partner reassignment is indistinguishable from an ordinary field edit and
    // every deal event has to refetch the roster. Unlike the HX-Trigger it
    // replaces, that fires for every deal mutation by every user on the client,
    // so a burst is collapsed into one refresh on ~1s of quiet.
    //
    // #filter-bar is deliberately not re-selected (refreshListResultsWrapper
    // does re-select it): it holds the partner search input, and swapping it
    // would discard what the rep is typing. #tabs-bar carries the per-type
    // counts, so it does ride along.
    // The debounce alone does not make the roster safe from a lost refresh: it
    // only collapses events that arrive within ~1s of each other, so a deal
    // event landing after the quiet window but before the roster round-trip
    // returns issues a second htmx.ajax while the first is still in flight.
    // htmx keys its request state on the source element and `swap: outerHTML`
    // detaches #list-results-wrapper, so that second call is queued against the
    // node about to be detached (htmx.js:4373-4388, default `queue last`), the
    // queue is drained only after the swap (endRequestLock in xhr.onload,
    // htmx.js:4602), and the re-issued request then dies at
    // `if (!bodyContains(elt)) return` (htmx.js:4284) with no request, no swap
    // and no console error — every partner's deal_count stays stale until the
    // next deal event. So the debounce hands off to an in-flight + dirty pair,
    // the same shape refreshDealTabs uses: while a refresh is running, a later
    // one sets the dirty flag and the settle handler re-runs it against a
    // freshly resolved wrapper. htmx.ajax's promise resolves after the swap and
    // rejects on send error / abort / timeout, so both settlement paths clear
    // the flag and nothing is stranded.
    var PARTNER_LIST_REFRESH_DEBOUNCE_MS = 1000;
    var partnerListRefreshTimer = null;
    var partnerListRefreshInFlight = false;
    var partnerListRefreshDirty = false;

    function refreshPartnerListDebounced() {
        if (!isOnPartnerList()) return;
        if (partnerListRefreshTimer) clearTimeout(partnerListRefreshTimer);
        partnerListRefreshTimer = setTimeout(function () {
            partnerListRefreshTimer = null;
            refreshPartnerList();
        }, PARTNER_LIST_REFRESH_DEBOUNCE_MS);
    }

    function refreshPartnerList() {
        if (!isOnPartnerList() || !window.htmx) return;
        if (partnerListRefreshInFlight) {
            partnerListRefreshDirty = true;
            return;
        }
        // Everything requested up to here is covered by the run below — or by
        // nothing at all, if the roster has since left the page.
        partnerListRefreshDirty = false;
        var wrapper = document.getElementById('list-results-wrapper');
        if (!wrapper) return;
        partnerListRefreshInFlight = true;
        var settled = function () {
            partnerListRefreshInFlight = false;
            if (partnerListRefreshDirty) refreshPartnerList();
        };
        var request = htmx.ajax('GET', window.location.href, {
            source: wrapper,
            target: wrapper,
            select: '#list-results-wrapper',
            selectOOB: '#tabs-bar',
            swap: 'outerHTML',
            push: 'false',
        });
        if (request && request.then) {
            request.then(settled, settled);
        } else {
            settled();
        }
    }

    function refreshLeadTabs() {
        refreshFragment('tabs-bar', '/acqs/leads/tabs/' + listQuery());
    }

    function refreshProspectTabs() {
        refreshFragment('tabs-bar', '/acqs/prospects/tabs/' + listQuery());
    }

    function refreshLeadQuickFilters() {
        refreshFragment('quick-filters', '/acqs/leads/quick-filters/' + listQuery());
    }

    function refreshExplicitlySortedLeadList() {
        if (!isOnLeadList() || !window.htmx) return false;
        var sort = new URLSearchParams(window.location.search).get('sort');
        if (['age', 'next_action', '-next_action', 'stage', '-stage'].indexOf(sort) === -1) {
            return false;
        }
        var wrapper = document.getElementById('list-results-wrapper');
        if (!wrapper) return false;
        htmx.ajax('GET', window.location.href, {
            source: wrapper,
            target: wrapper,
            select: '#list-results-wrapper',
            selectOOB: '#filter-bar, #tabs-bar',
            swap: 'outerHTML',
            push: 'false',
        });
        return true;
    }

    function refreshProspectQuickFilters() {
        refreshFragment('quick-filters', '/acqs/prospects/quick-filters/' + listQuery());
    }

    // The immediate lead_updated refresh races the asynchronous search-index
    // update. When the index catches up, re-fetch the full filtered-list
    // surface (rather than one row) so membership, pagination, filters, tabs,
    // and quick-filter counts all converge on the same query result.
    var LEAD_SEARCH_INDEX_REFRESH_DEBOUNCE_MS = 300;
    var leadSearchIndexRefreshTimer = null;

    function isSearchFilteredListForPipeline(pipeline) {
        var query = new URLSearchParams(window.location.search).get('q');
        if (!query || !query.trim()) return false;
        if (pipeline === 'prospect') return isOnProspectList();
        if (pipeline === 'lead') return isOnLeadList();
        return isOnLeadList() || isOnProspectList();
    }

    function refreshListResultsWrapper() {
        if (!window.htmx) return;
        var wrapper = document.getElementById('list-results-wrapper');
        if (!wrapper) return;
        htmx.ajax('GET', window.location.href, {
            source: wrapper,
            target: wrapper,
            select: '#list-results-wrapper',
            selectOOB: '#filter-bar, #tabs-bar',
            swap: 'outerHTML',
            push: 'false',
        });
    }

    function refreshSearchFilteredListAfterIndex(pipeline) {
        if (!isSearchFilteredListForPipeline(pipeline)) return;
        if (leadSearchIndexRefreshTimer) clearTimeout(leadSearchIndexRefreshTimer);
        leadSearchIndexRefreshTimer = setTimeout(function () {
            leadSearchIndexRefreshTimer = null;
            if (!isSearchFilteredListForPipeline(pipeline)) return;
            refreshListResultsWrapper();
        }, LEAD_SEARCH_INDEX_REFRESH_DEBOUNCE_MS);
    }

    function refreshVisibleLeadOrProspectRow(leadId) {
        if (isOnLeadList()) refreshLeadRow(leadId);
        if (isOnProspectList()) refreshProspectRow(leadId);
    }

    // Every badge matching `selector` takes `source`'s `data-unread-count` (0 when
    // `source` is missing): text, `data-unread-count` and hidden, set in place so no
    // badge is replaced by another's markup.
    function setUnreadBadges(selector, source) {
        var count = source ? parseInt(source.getAttribute('data-unread-count') || '0', 10) : 0;
        document.querySelectorAll(selector).forEach(function (badge) {
            badge.textContent = count;
            badge.setAttribute('data-unread-count', count);
            badge.hidden = !count;
        });
    }

    // The SMS rail button on the lead and prospect pages and the SMS icon in the
    // sheet's action bar carry the lead-wide unread count, read off the lead SMS
    // card's `data-unread-count` (acqs:lead-messages-card).
    function setRailSmsUnread(leadId, card) {
        setUnreadBadges('[data-lead-sms-unread="' + leadId + '"]', card);
    }

    function refreshRailSmsUnread(leadId) {
        if (!document.querySelector('[data-lead-sms-unread="' + leadId + '"]')) return;
        fetch('/acqs/leads/' + leadId + '/messages/card/', { headers: { 'HX-Request': 'true' } })
            .then(function (r) {
                if (!r.ok) throw new Error('messages card ' + r.status);
                return r.text();
            })
            .then(function (html) {
                var tpl = document.createElement('template');
                tpl.innerHTML = html;
                setRailSmsUnread(leadId, tpl.content.querySelector('[data-lead-messages-card]'));
            })
            .catch(function (err) { console.warn('rail SMS unread refresh failed', err); });
    }

    // The buyer page's SMS rail badge (acqs/_buyer_sms_unread_badge.html) and the SMS icon
    // in the buyer and partner sheets' action bars (acqs/_partner_sms_unread_badge.html) carry
    // the buyer-wide or partner-wide unread count. There is no Messages card to read it off,
    // so each refetches the badge from the conversation view's `?part=badge`
    // (`data-badge-url`). `kind` is 'buyer' or 'partner'; the badges are
    // `[data-<kind>-sms-unread="<pk>"]`, refreshed after a conversation opens or on an
    // incoming text.
    function refreshTargetSmsUnread(kind, pk) {
        var attr = 'data-' + kind + '-sms-unread';
        document.querySelectorAll('[' + attr + '="' + pk + '"]').forEach(function (badge) {
            var url = badge.getAttribute('data-badge-url');
            if (!url) return;   // still the lazy placeholder; its own load brings the count
            fetch(url, { headers: { 'HX-Request': 'true' } })
                .then(function (r) {
                    if (!r.ok) throw new Error(kind + ' SMS badge ' + r.status);
                    return r.text();
                })
                .then(function (html) {
                    var tpl = document.createElement('template');
                    tpl.innerHTML = html;
                    var next = tpl.content.querySelector('[' + attr + ']');
                    var count = next ? parseInt(next.getAttribute('data-unread-count') || '0', 10) : 0;
                    badge.textContent = count;
                    badge.setAttribute('data-unread-count', count);
                    badge.hidden = !count;
                })
                .catch(function (err) { console.warn(kind + ' SMS unread refresh failed', err); });
        });
    }

    function refreshCardsForOpenedConversations(root) {
        var scope = root && root.querySelectorAll ? root : document;
        var conversations = [];
        if (scope.matches && scope.matches('[data-acqs-conversation][data-refresh-messages-card]')) {
            conversations.push(scope);
        }
        scope.querySelectorAll('[data-acqs-conversation][data-refresh-messages-card]').forEach(function (conversation) {
            conversations.push(conversation);
        });
        conversations.forEach(function (conversation) {
            conversation.removeAttribute('data-refresh-messages-card');
            refreshRailSmsUnread(conversation.getAttribute('data-lead-id'));
        });
    }

    // Opening or switching a buyer or partner conversation marks a thread read, so its SMS
    // badges (the buyer page rail button and the sheet's action-bar icon) recount. The root
    // carries `data-refresh-<kind>-badge` and `data-<kind>-id`.
    function refreshTargetBadgesForOpenedConversations(root) {
        var scope = root && root.querySelectorAll ? root : document;
        ['buyer', 'partner'].forEach(function (kind) {
            var selector = '[data-acqs-conversation][data-refresh-' + kind + '-badge]';
            var conversations = [];
            if (scope.matches && scope.matches(selector)) conversations.push(scope);
            scope.querySelectorAll(selector).forEach(function (conversation) {
                conversations.push(conversation);
            });
            conversations.forEach(function (conversation) {
                conversation.removeAttribute('data-refresh-' + kind + '-badge');
                refreshTargetSmsUnread(kind, conversation.getAttribute('data-' + kind + '-id'));
            });
        });
    }

    // Recipient switches, line tabs (_lead_sms_line_tabs.html), From rows
    // (_lead_sms_line_picker.html) and the draft slot's Open draft (acqs/_bot_drafts.html) replace the
    // whole conversation surface, and on a lead's SMS sheet the draft slot too. Ask only when the active
    // composer has a draft or a draft card there is open in its editor (`data-bot-editing`); cancelling
    // leaves both the text and radio selection untouched. Accepted switches close
    // through SeeMe so a portaled menu is restored before its conversation root is replaced.
    document.addEventListener('click', function (event) {
        var input = event.target.closest && event.target.closest('[data-recipient-switch], [data-line-switch]');
        if (!input) return;
        var isLine = input.hasAttribute('data-line-switch');
        var menu = input.closest('.dropdown-menu');
        var isCurrent = input.hasAttribute('data-current-line-id')
            ? input.value === input.getAttribute('data-current-line-id')
            : isLine
                ? input.classList.contains('tabs-button--active')
                : input.value === input.getAttribute('data-current-phone-id');
        if (!isCurrent) {
            var composer = document.querySelector(input.getAttribute('data-composer-target'));
            var textarea = composer && composer.querySelector('textarea[name="message"]');
            var sheet = composer && composer.closest('.sheet');
            var editing = sheet && sheet.querySelector('[data-bot-drafts-moved] [data-bot-editing]');
            var unsent = textarea && textarea.value.trim();
            var confirmMessage = editing
                ? 'Switch threads and close the bot draft you are editing?  Your unsaved changes to it are lost.'
                : isLine
                    ? 'Switch lines and discard this unsent message?'
                    : 'Switch recipients and discard this unsent message?';
            if ((unsent || editing) && !window.confirm(confirmMessage)) {
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }
        } else {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
        if (menu && window._cc && window._cc.closeDropdown) {
            window._cc.closeDropdown(menu);
        }
    }, true);

    document.addEventListener('sheet:ready', function (event) {
        refreshCardsForOpenedConversations(event.target);
        refreshTargetBadgesForOpenedConversations(event.target);
    });
    document.body.addEventListener('htmx:afterSwap', function () {
        refreshCardsForOpenedConversations(document);
        refreshTargetBadgesForOpenedConversations(document);
    });

    // The Text item in the buyer page's Contacts block (_phone_call_menu.html with
    // `buyer_sms.surface == 'rail'`) opens the page's SMS rail section on that number.
    // shell.js opens a section only from a rail-button click or the #rail-<name> hash, so
    // this clicks the SMS rail button (unless the section is already open) and loads the
    // panel on the number: it reloads the panel root, or, before the section's first open,
    // replaces the lazy placeholder so its `intersect once` request never fires as well.
    // A different number with an unsent draft asks first, like a picker switch.
    document.addEventListener('click', function (event) {
        var link = event.target.closest && event.target.closest('[data-buyer-sms-open]');
        if (!link) return;
        event.preventDefault();
        var menu = link.closest('.dropdown-menu');
        if (menu && window._cc && window._cc.closeDropdown) window._cc.closeDropdown(menu);
        var panel = document.getElementById('shell-panel');
        var railBtn = document.querySelector('#shell-rail [data-rail="sms"]');
        if (!panel || !railBtn || !window.htmx) return;
        var buyerId = link.getAttribute('data-buyer-sms-open');
        var url = link.getAttribute('data-conversation-url');
        var root = document.getElementById('buyer-conversation-panel-' + buyerId);
        var placeholder = panel.querySelector('[data-panel-section="sms"] [data-buyer-sms-placeholder]');
        if (root) {
            if (root.getAttribute('data-selected-phone-id') !== link.getAttribute('data-phone-id')) {
                var textarea = document.querySelector('#buyer-sms-composer textarea[name="message"]');
                if (textarea && textarea.value.trim() && !window.confirm('Switch recipients and discard this unsent message?')) {
                    return;
                }
                htmx.ajax('GET', url, { target: root, swap: 'outerHTML' });
            }
        } else if (placeholder) {
            var fresh = placeholder.cloneNode(true);
            ['hx-get', 'hx-trigger', 'hx-swap', 'data-buyer-sms-placeholder'].forEach(function (name) {
                fresh.removeAttribute(name);
            });
            placeholder.replaceWith(fresh);
            htmx.ajax('GET', url, { target: fresh, swap: 'outerHTML' });
        }
        if (panel.hidden || panel.getAttribute('data-panel-current') !== 'sms') railBtn.click();
    });

    // ── Email on a lead, prospect, buyer or partner ────────────────────────
    // One set of email surfaces serves every kind (acqs/views/email_conversation.py).
    // Each refreshable element names its target with `data-email-kind` ('lead',
    // 'buyer' or 'partner') and `data-email-pk`, and carries its own `data-refresh-url`. The
    // Email rail button's badge on the lead, prospect and buyer pages and the email
    // icon in their sheets' action bars carry the viewer's unread email count for the
    // target, its own Gmail plus shared mailboxes (`email_inbox.counted_q`)
    // (`data-lead-email-unread` / `data-buyer-email-unread` / `data-partner-email-unread`), read off a freshly
    // rendered list's or panel's `data-unread-count`.
    function setRailEmailUnread(kind, pk, source) {
        setUnreadBadges('[data-' + kind + '-email-unread="' + pk + '"]', source);
    }

    // Badges only, when no thread list is on screen to ride along with. Each badge
    // carries the unread route (acqs:<kind>-email-unread), which returns one badge.
    function refreshRailEmailUnread(kind, pk) {
        var badge = document.querySelector('[data-' + kind + '-email-unread="' + pk + '"][data-refresh-url]');
        if (!badge) return;
        fetch(badge.getAttribute('data-refresh-url'), { headers: { 'HX-Request': 'true' } })
            .then(function (r) {
                if (!r.ok) throw new Error('email unread ' + r.status);
                return r.text();
            })
            .then(function (html) {
                var tpl = document.createElement('template');
                tpl.innerHTML = html;
                var next = tpl.content.querySelector('[data-unread-count]');
                if (next) setRailEmailUnread(kind, pk, next);
            })
            .catch(function (err) { console.warn('rail email unread refresh failed', err); });
    }

    // An open thread sheet's messages (acqs/_lead_email_thread_messages.html).  What the rep expanded stays
    // expanded.  What follows the messages stays put on screen when they grow above a rep who is reading
    // further down.  A focused field in them (a QA note) holds the swap until the next refresh.
    function refreshThreadMessages(el) {
        var container = scrollContainerOf(el);
        var anchor = null;
        var anchorTop = 0;
        var target = function () {
            return el.isConnected && !el.contains(document.activeElement) ? el : null;
        };
        var prepare = function (html) {
            var next = new DOMParser().parseFromString(html, 'text/html').body.firstElementChild;
            el.querySelectorAll('.collapse[id]').forEach(function (part) {
                var copy = next.querySelector('[id="' + part.id + '"]');
                if (copy) copy.classList.toggle('collapse--open', part.classList.contains('collapse--open'));
            });
            el.querySelectorAll('[data-collapse-target][aria-expanded]').forEach(function (heading) {
                var copy = next.querySelector('[data-collapse-target="' + heading.getAttribute('data-collapse-target') + '"]');
                if (copy) copy.setAttribute('aria-expanded', heading.getAttribute('aria-expanded'));
            });
            if (container && el.getBoundingClientRect().bottom < container.getBoundingClientRect().top + container.clientHeight / 2) {
                anchor = el.nextElementSibling;
                anchorTop = anchor ? anchor.getBoundingClientRect().top : 0;
            }
            return next.outerHTML;
        };
        return refreshFragment(target, el.getAttribute('data-refresh-url'), function (next) {
            var sheet = next.closest('.sheet');
            var count = sheet && sheet.querySelector('[data-email-thread-count]');
            if (count) count.textContent = next.getAttribute('data-message-count-label');
            if (anchor && anchor.isConnected) container.scrollTop += anchor.getBoundingClientRect().top - anchorTop;
        }, prepare);
    }

    // Every open email surface of one lead, buyer or partner re-fetches itself from its
    // `data-refresh-url`: the Emails list sheet's list
    // (acqs/_lead_email_list.html) and the page rail panel
    // (acqs/_lead_email_panel.html) while it shows the thread list. A panel showing
    // a thread or the composer is left alone, since a reply or a draft may be in
    // progress; only the rail badge moves. Each target is kept as its exact node,
    // so a list and a panel never trade places and a panel the rep
    // switched to a thread mid-request is not overwritten.
    // `opening` is the thread sheet's own open, whose messages just rendered.
    function refreshTargetEmails(kind, pk, opening) {
        var badgeRidesAlong = false;
        var match = '[data-email-kind="' + kind + '"][data-email-pk="' + pk + '"]';
        if (!opening) document.querySelectorAll('[data-email-thread-messages]' + match).forEach(refreshThreadMessages);
        document.querySelectorAll(
            '[data-email-list]' + match + ', [data-email-panel]' + match
        ).forEach(function (el) {
            if (el.hasAttribute('data-email-panel') && el.getAttribute('data-panel-view') !== 'list') return;
            var url = el.getAttribute('data-refresh-url');
            if (!url) return;
            // A panel that started loading a thread meanwhile (`data-panel-view="loading"`,
            // email-reply.js) keeps it: the click's own swap lands instead.
            var target = function () {
                if (!el.isConnected) return null;
                return el.hasAttribute('data-email-panel') && el.getAttribute('data-panel-view') !== 'list' ? null : el;
            };
            if (refreshFragment(target, url, function (next) { setRailEmailUnread(kind, pk, next); })) {
                badgeRidesAlong = true;
            }
        });
        if (!badgeRidesAlong) refreshRailEmailUnread(kind, pk);
    }

    // After a reconnect, any `email_synced` sent while the socket was down is lost,
    // so every lead, buyer and partner with an email list, panel or badge on the
    // page refreshes once.
    function refreshAllTargetEmails() {
        var targets = {};
        document.querySelectorAll('[data-email-kind][data-email-pk]').forEach(function (el) {
            targets[el.getAttribute('data-email-kind') + ':' + el.getAttribute('data-email-pk')] = true;
        });
        ['lead', 'buyer', 'partner'].forEach(function (kind) {
            document.querySelectorAll('[data-' + kind + '-email-unread]').forEach(function (el) {
                targets[kind + ':' + el.getAttribute('data-' + kind + '-email-unread')] = true;
            });
        });
        Object.keys(targets).forEach(function (key) {
            var parts = key.split(':');
            refreshTargetEmails(parts[0], parts[1]);
        });
    }

    // Opening a thread sheet may clear its unread count (when the count is the
    // viewer's, `email_conversation.open_thread`), but the sheet response
    // cannot carry the page's rail badge out of band (sheets.js loads it with a
    // plain XHR), so the thread sheet's `data-email-thread-opened` marker
    // refreshes the page's badge and thread lists underneath.
    document.addEventListener('sheet:ready', function (event) {
        var marker = event.target.querySelector && event.target.querySelector('[data-email-thread-opened]');
        if (marker) refreshTargetEmails(marker.getAttribute('data-email-kind'), marker.getAttribute('data-email-pk'), true);
    });

    // A draft's opener lands on the Emails list (`email_list_sheet`, `open_draft`) and goes on to the
    // reply draft's thread.  The list first drops the param, so Back to it stays there.
    document.addEventListener('sheet:ready', function (event) {
        var marker = event.target.querySelector && event.target.querySelector('[data-email-open-draft]');
        if (!marker || !window.rfSheet) return;
        window.rfSheet.setUrl(marker.getAttribute('data-email-list-url'));
        window.rfSheet.open(marker.getAttribute('data-email-open-draft'));
    });

    // A lead's conversation sheet names its thread (`data-sheet-url`, _lead_conversation_sheet.html).  After a
    // To or From switch, or a refresh, the page's ?sheet= param follows it, so the link opens that thread.  The
    // first load is not an htmx swap, so a Bot decisions link (?decision=) keeps its URL until something swaps.
    document.body.addEventListener('htmx:afterSettle', function () {
        var root = document.querySelector('.sheet__body [data-lead-timeline][data-sheet-url]');
        if (!root || !window.rfSheet) return;
        var url = root.getAttribute('data-sheet-url');
        var current = new URLSearchParams(window.location.search).get('sheet');
        // Only while this sheet is the open one: a closed sheet's left-over body must not bring ?sheet= back.
        if (current && current !== url && current.split('?')[0] === url.split('?')[0]) window.rfSheet.setUrl(url);
    });

    // A conversation timeline renders oldest first, so it reads from the bottom like
    // a phone. Every newly rendered timeline (sheet open, panel load, recipient
    // switch, send) scrolls its scroll container to the newest message once. A
    // hidden panel timeline waits for `shell:panel-open`. rAF runs after sheets.js
    // resets the sheet body's scrollTop to 0. A live refresh keeps the reader's
    // place unless they were already at the bottom (`refreshOpenConversation`).
    // A Bot decisions row's sheet (`data-focus-decision`) opens at that decision instead.
    var NEAR_BOTTOM_PX = 80;

    function scrollContainerOf(el) {
        for (var node = el.parentElement; node && node !== document.body; node = node.parentElement) {
            var overflowY = getComputedStyle(node).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') return node;
        }
        return null;
    }

    function isNearBottom(container) {
        return container.scrollHeight - container.clientHeight - container.scrollTop <= NEAR_BOTTOM_PX;
    }

    function scrollTimelineToNewest(timeline) {
        var container = scrollContainerOf(timeline);
        if (!container) return;
        container.scrollTop = container.scrollHeight;
        // MMS images load after this and push the newest message down.
        timeline.querySelectorAll('img').forEach(function (img) {
            if (img.complete) return;
            img.addEventListener('load', function () {
                container.scrollTop = container.scrollHeight;
            }, { once: true });
        });
    }

    // Center a decision in its sheet: its row in the timeline, with any quiet fold around it and its
    // own detail opened, the text bubble it sent, or its draft card at the thread's end.
    // False when the sheet does not show it.
    function scrollToFocusedDecision(timeline, pk) {
        var container = scrollContainerOf(timeline);
        var target = container && container.querySelector(
            '[data-decision-row="' + pk + '"], [data-bot-decision="' + pk + '"], #eval-event-' + pk);
        if (!target) return false;
        [target.closest('.collapse'), target.querySelector(':scope > .chat-log__event-detail')].forEach(function (fold) {
            if (!fold || fold.classList.contains('collapse--open')) return;
            var trigger = container.querySelector('[data-collapse-target="#' + fold.id + '"]');
            if (trigger) trigger.click();
        });
        var rect = target.getBoundingClientRect();
        var box = container.getBoundingClientRect();
        container.scrollTop += (rect.top + rect.bottom - box.top - box.bottom) / 2;
        return true;
    }

    function scrollNewConversationTimelines() {
        requestAnimationFrame(function () {
            document.querySelectorAll('[data-conversation-timeline]').forEach(function (timeline) {
                if (timeline._acqsScrolled || !timeline.offsetParent) return;
                timeline._acqsScrolled = true;
                var pk = timeline.getAttribute('data-focus-decision');
                if (!pk || !scrollToFocusedDecision(timeline, pk)) scrollTimelineToNewest(timeline);
            });
        });
    }

    document.addEventListener('htmx:load', scrollNewConversationTimelines);
    document.addEventListener('sheet:ready', scrollNewConversationTimelines);
    document.addEventListener('shell:panel-open', scrollNewConversationTimelines);

    // A lead sheet's fixed controls (acqs/_lead_conversation_controls.html) arrive in a `part=root`
    // response marked out of band: swap them into place and return the rest of the response.
    function swapConversationControls(html) {
        if (html.indexOf('data-conversation-controls') === -1) return html;
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var fresh = tpl.content.querySelector('[data-conversation-controls]');
        var live = fresh && document.getElementById(fresh.id);
        if (fresh) fresh.remove();
        if (live) {
            fresh.removeAttribute('hx-swap-oob');
            swapFragment(live, fresh.outerHTML);
        }
        return tpl.innerHTML;
    }

    // Two conversations can be open for one owner: the conversation sheet
    // (#lead-conversation-sheet-<pk>) and the lead or prospect page's SMS panel
    // section (#lead-conversation-panel-<pk>), or a buyer's
    // (#buyer-conversation-sheet-<pk>, #buyer-conversation-panel-<pk>). Each reloads its whole conversation
    // root (`part=root`: picker, timeline, and the To field's unread badge for the
    // other numbers), so a text on a number other than the selected one still shows.
    // The composer is outside the root, so a draft survives. A lead's sheet has its chips and
    // To and From pickers in fixed controls, lifted out of the root; they ride along (`swapConversationControls`). Only a visible one
    // marks the shown line's thread read; the panel stays in the DOM while hidden.
    // `selector` picks the roots: a lead conversation by its one owner (`data-owner-id`),
    // a buyer's by the buyer (`data-buyer-id`), a partner's by the partner
    // (#partner-conversation-sheet-<pk>, `data-partner-id`).
    function refreshOpenConversation(selector, callback) {
        var refreshed = false;
        document.querySelectorAll(selector).forEach(function (convo) {
            var url = convo.getAttribute('data-refresh-url');
            if (!url) return;
            // An open picker menu is portaled to <body>; replacing the root under
            // it would orphan the menu. The next switch or text refreshes it.
            var controls = document.getElementById('lead-conversation-controls-' + convo.getAttribute('data-lead-id'));
            var menuOpen = Array.prototype.some.call(document.querySelectorAll('.dropdown-menu.show'), function (menu) {
                var owner = window._cc && window._cc.ownerOf ? window._cc.ownerOf(menu, '[data-acqs-conversation], [data-conversation-controls]') : null;
                return owner === convo || (owner && owner === controls);
            });
            if (menuOpen) return;
            var visible = !convo.closest('[hidden]');
            var query = '&mark_read=' + (visible ? '1' : '0');
            // Keep this exact root: a recipient switch mid-request swaps in a new
            // root with the same id, and the old number's thread must not land in it.
            var target = function () { return convo.isConnected ? convo : null; };
            // Follow the thread only when the reader was already at its bottom; a
            // reader scrolled up into history keeps their place.
            var container = scrollContainerOf(convo);
            var follow = !container || isNearBottom(container);
            var scrollTop = container ? container.scrollTop : 0;
            // A fold the reader opened in the timeline stays open.
            var openFolds = Array.prototype.map.call(convo.querySelectorAll('.collapse.collapse--open[id]'), function (el) { return el.id; });
            // The callback refreshes cards and rows once, after the first conversation.
            var first = !refreshed;
            var done = function (next, el) {
                openFolds.forEach(function (id) {
                    var fold = next && next.querySelector('#' + id);
                    if (fold) fold.classList.add('collapse--open');
                });
                var timeline = next && next.querySelector('[data-conversation-timeline]');
                if (timeline && timeline.offsetParent) {
                    timeline._acqsScrolled = true;
                    if (follow) scrollTimelineToNewest(timeline);
                    else if (container) container.scrollTop = scrollTop;
                }
                if (first && callback) callback(next, el);
            };
            if (refreshFragment(target, url + query, done, swapConversationControls)) refreshed = true;
        });
        return refreshed;
    }

    // A seller draft's Reject and Discard confirms ask why (bot_review/_entry_reason_input.html).  A blank
    // reason never posts: the click stops here, in the capture phase before htmx and the menu's close
    // handler, and the confirm shows its `data-reason-blank` sentence under the box.  The view's own 422
    // for a blank reason (plain text, `HX-Reswap: none`) lands in the same place.
    function reasonBox(el) {
        return el.closest('.dropdown-menu, [data-menu-panel]');
    }
    function showReasonError(box, message) {
        var slot = box && box.querySelector('[data-reason-error]');
        if (!slot) return;
        slot.textContent = message || '';
        slot.hidden = !message;
    }
    document.addEventListener('click', function (e) {
        var submit = e.target.closest && e.target.closest('[data-reason-submit]');
        if (!submit) return;
        var box = reasonBox(submit);
        var input = box && box.querySelector('[data-reason-input]');
        if (!input || input.value.trim()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        showReasonError(box, submit.getAttribute('data-reason-blank'));
        input.focus();
    }, true);
    document.addEventListener('input', function (e) {
        var input = e.target.closest && e.target.closest('[data-reason-input]');
        if (input && input.value.trim()) showReasonError(reasonBox(input), '');
    });
    document.body.addEventListener('htmx:afterRequest', function (e) {
        var elt = e.detail.elt;
        var xhr = e.detail.xhr;
        if (!elt || !elt.hasAttribute || !elt.hasAttribute('data-reason-submit') || !xhr || xhr.status !== 422) return;
        if ((xhr.getResponseHeader('Content-Type') || '').indexOf('text/plain') !== 0) return;
        showReasonError(reasonBox(elt), xhr.responseText.trim());
    });

    // A lead's SMS sheet and the lead page's SMS panel show bot decisions too, so a bot change refreshes
    // them, and a text refreshes them even on a lead with no owner, which `data-owner-id` misses.
    var LEAD_TIMELINE_REFRESH_DEBOUNCE_MS = 300;
    var leadTimelineTimers = {};

    // Another tab or the bot changed a lead's run or drafts: the Bot and Draft waiting chips and the
    // rail draft badges (`data-bot-lead-id`) re-fetch themselves on `acqsBotRunChanged`, and the rail
    // panels re-render their root, which carries the waiting-draft alert.
    function refreshBotLeadChrome(leadId) {
        if (document.querySelector('[data-bot-lead-id="' + leadId + '"]') && window.htmx) {
            htmx.trigger(document.body, 'acqsBotRunChanged');
        }
        refreshOpenConversation('[data-acqs-conversation]:not([data-lead-timeline])[data-lead-id="' + leadId + '"]');
        if (document.querySelector('[data-email-panel][data-email-kind="lead"][data-email-pk="' + leadId + '"]')) {
            refreshTargetEmails('lead', String(leadId));
        }
    }

    function refreshLeadTimeline(leadId) {
        if (leadTimelineTimers[leadId]) clearTimeout(leadTimelineTimers[leadId]);
        leadTimelineTimers[leadId] = setTimeout(function () {
            delete leadTimelineTimers[leadId];
            refreshOpenConversation('[data-acqs-conversation][data-lead-timeline][data-lead-id="' + leadId + '"]', function () {
                refreshRailSmsUnread(leadId);
            });
        }, LEAD_TIMELINE_REFRESH_DEBOUNCE_MS);
    }

    // When a property is edited (e.g. address change), any lead/prospect row
    // displaying that property as its primary needs to be refreshed too.
    // Rows stamp themselves with data-primary-property-id="<id>" so we can
    // find matching rows by attribute lookup without server-side coupling.
    // One entry per entity whose row stamps data-primary-property-id. The
    // matchers are mutually exclusive (a row id can only carry one prefix), so
    // at most one entry ever fires per row.
    var PRIMARY_PROPERTY_ROW_ENTITIES = {
        lead: { matcher: /^lead-(\d+)$/, isOnList: isOnLeadList, refresh: refreshLeadRow },
        prospect: { matcher: /^prospect-(\d+)$/, isOnList: isOnProspectList, refresh: refreshProspectRow },
        deal: { matcher: /^deal-(\d+)$/, isOnList: isOnDealList, refresh: refreshDealRow },
    };

    function refreshRowsForProperty(propertyId) {
        var selector = '[data-primary-property-id="' + propertyId + '"]';
        document.querySelectorAll(selector).forEach(function (row) {
            Object.keys(PRIMARY_PROPERTY_ROW_ENTITIES).forEach(function (prefix) {
                var entity = PRIMARY_PROPERTY_ROW_ENTITIES[prefix];
                var m = row.id.match(entity.matcher);
                if (m && entity.isOnList()) entity.refresh(parseInt(m[1], 10));
            });
        });
    }

    // ── Signal ─────────────────────────────────────────────────────────────

    function refreshSignalRow(signalId) {
        refreshRow('signal-' + signalId, '/acqs/signals/' + signalId + '/row/');
    }

    function removeSignalRow(signalId) {
        removeRow(document.getElementById('signal-' + signalId));
    }

    // ── Proposal ───────────────────────────────────────────────────────────

    // htmx.ajax, not a raw XHR + `card.outerHTML = ...`: the negotiation card carries
    // an inline <script> that mirrors the tag-input chips into the form's hidden
    // inputs, and scripts inserted via innerHTML/outerHTML never execute. Assigning
    // outerHTML left the buy/list multi-select posting whatever set the server last
    // rendered — a chip added or removed after a WS refresh vanished from the DOM,
    // toasted "Saved", and never reached the DB. htmx re-creates script nodes,
    // re-processes hx-* attributes, and fires the swap events app listeners expect.
    //
    // htmx.process() alone won't fix this either — it only rewires hx-* attributes, not
    // <script> tags. htmx.ajax's fragment builder (makeFragment → normalizeScriptTags) is
    // what recreates the script node so it actually executes.
    function refreshProposalsCard(leadId) {
        if (!window.htmx) return;
        surfaceCards('lead-negotiation-card').forEach(function (card) {
            htmx.ajax('GET', '/acqs/leads/' + leadId + '/proposals/card/', {
                source: card,
                target: card,
                swap: 'outerHTML',
            });
        });
    }

    // ── Images ─────────────────────────────────────────────────────────────

    // Background room classification, a cloud-import image landing, or any
    // other images mutation fired a narrow images-refresh event. If THIS tab
    // has that property's images card on screen, re-fetch the card and swap
    // just the gallery — no full sheet refresh (which would clobber other
    // targeted swaps / in-progress edits, per the property_updated NOTE
    // above), and no full-card swap either (which would wipe an open manual
    // uploader or cloud-import form mid-import).
    function propertyImagesCardFor(propertyId) {
        // First card that actually represents this property, not simply the
        // first one in the document — with a page and a sheet both open, those
        // are different cards.
        var cards = surfaceCards('property-images');
        for (var i = 0; i < cards.length; i++) {
            var card = cards[i];
            var representedIds = (card.getAttribute('data-image-property-ids') || card.getAttribute('data-property-id') || '')
                .split(',')
                .map(function (id) { return id.trim(); })
                .filter(Boolean);
            if (representedIds.indexOf(String(propertyId)) !== -1) return card;
        }
        return null;
    }

    // A cloud import broadcasts once per imported image — coalesce bursts with
    // a debounce (trailing ~1s of quiet) capped by a max-wait (~3s from the
    // first deferred event), so a sustained faster-than-1/sec burst still
    // repaints at least every ~3s (visible incremental growth) and a final
    // trailing refresh catches the last state. The property-id match check
    // still runs per-event, before the timer, so a mismatched event never
    // schedules (or cancels into) a refresh it wouldn't have triggered.
    var IMAGES_REFRESH_QUIET_MS = 1000;
    var IMAGES_REFRESH_MAX_WAIT_MS = 3000;
    var imagesCardRefreshTimer = null;
    var imagesCardRefreshDeadline = 0;  // 0 = no refresh currently deferred
    function refreshPropertyImagesCardDebounced(propertyId) {
        if (!propertyImagesCardFor(propertyId)) return;
        var now = Date.now();
        if (!imagesCardRefreshDeadline) imagesCardRefreshDeadline = now + IMAGES_REFRESH_MAX_WAIT_MS;
        var delay = Math.max(0, Math.min(IMAGES_REFRESH_QUIET_MS, imagesCardRefreshDeadline - now));
        if (imagesCardRefreshTimer) clearTimeout(imagesCardRefreshTimer);
        imagesCardRefreshTimer = setTimeout(function () {
            imagesCardRefreshTimer = null;
            imagesCardRefreshDeadline = 0;
            refreshPropertyImagesCard(propertyId);
        }, delay);
    }

    function refreshPropertyImagesCard(propertyId) {
        var card = propertyImagesCardFor(propertyId);
        if (!card) return;
        var renderPropertyId = card.getAttribute('data-property-id');
        var leadId = card.getAttribute('data-acq-lead-id');
        // The card owns its read route. A deal viewer may receive a
        // deal-scoped URL here because they have deal access but not generic
        // property-media permission; never reconstruct the broader endpoint.
        var url = card.getAttribute('data-media-refresh-url');
        if (!url) return;
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.onload = function () {
            if (xhr.status < 200 || xhr.status >= 300 || !xhr.responseText.trim()) return;
            // Staleness guard: the response was built for the card identified
            // by (renderPropertyId, leadId) above. If the user switched the
            // sheet to another record while the fetch was in flight, the live
            // #property-images is a NEW card with the same element id — bail
            // entirely (gallery swap AND count OOBs) rather than paint the old
            // record's gallery/counts into the new card.
            var live = propertyImagesCardFor(propertyId);
            if (!live
                || live.getAttribute('data-property-id') !== renderPropertyId
                || (live.getAttribute('data-acq-lead-id') || '') !== (leadId || '')
                || live.getAttribute('data-media-refresh-url') !== url) return;
            // The endpoint returns the card HTML plus OOB count fragments.
            var tmp = document.createElement('div');
            tmp.innerHTML = xhr.responseText.trim();
            var newCard = tmp.querySelector('#property-images') || tmp.firstElementChild;
            if (!newCard) return;
            var currentGallery = live.querySelector('#property-images-gallery');
            var newGallery = newCard.querySelector('#property-images-gallery');
            if (currentGallery && newGallery) {
                // Gallery-only swap — room labels live inside the gallery, so
                // classifier refreshes still land, while the surrounding card
                // (open uploader / cloud-import form / import status) is untouched.
                currentGallery.replaceWith(newGallery);
                if (window.htmx) htmx.process(newGallery);
                if (window._cc && window._cc.applyBrowserLocalDatetime) {
                    window._cc.applyBrowserLocalDatetime(newGallery);
                }
                // Drop the leftover card shell from tmp so the OOB scan below
                // only sees fragments appended AFTER the card — an
                // oob-annotated element INSIDE the card must not clobber the
                // live status poller/forms. (acqs-image-uploader.js gets the
                // same scoping because replaceWith moves its card out of tmp.)
                newCard.remove();
            } else {
                // Fallback: full-card replace (old DOM shape or unexpected
                // response). replaceWith moves newCard out of tmp, leaving
                // only the appended OOB fragments for the scan below.
                var parent = live.parentNode;
                if (!parent) return;
                live.replaceWith(newCard);
                if (window.htmx) htmx.process(parent);
                if (window._cc && window._cc.applyBrowserLocalDatetime) {
                    window._cc.applyBrowserLocalDatetime(newCard);
                }
            }
            // Swap the OOB count fragments appended after the card (outer
            // tab/title count badges) — imports change the image count, so
            // keep them live. Same manual OOB loop as acqs-image-uploader.js.
            tmp.querySelectorAll('[hx-swap-oob],[data-hx-swap-oob]').forEach(function (el) {
                var targetId = (el.getAttribute('hx-swap-oob') || el.getAttribute('data-hx-swap-oob') || '').replace(/^outerHTML:/, '');
                var target = targetId ? document.querySelector(targetId) : null;
                if (target) target.replaceWith(el);
            });
        };
        xhr.send();
    }

    // ── Telephony (call lifecycle) ──────────────────────────────────────────

    // A finished call wrote its completion AcqLog. If THIS tab has the linked
    // record's Activity card on screen — in a full detail page OR an open
    // side-sheet over the list — live-refresh just that card (outerHTML swap of
    // the existing partial), no full reload. `owner_updated` reuses it for the
    // owner card (a deleted owner note, say).
    //
    // Match by DOM, not URL: when a sheet is open the page URL is still the list
    // URL (e.g. /acqs/prospects/), so URL-based detail matching misses the card
    // that's right there in the DOM. Instead we locate the [data-call-activity]
    // cards whose data-lead-id / data-owner-id matches the event — page rail and
    // sheet alike — and re-fetch each through refreshActivityCards below.
    //
    // `leadId` is an AcqLead PK — could be a lead OR a prospect (both are AcqLead
    // rows). Only one of the lead/prospect card is ever in the DOM at a time, so
    // matching by the on-screen card's data-lead-id needs no pipeline
    // disambiguation.
    function refreshCallActivityCard(leadId, ownerId) {
        var lead = String(leadId == null ? '' : leadId).replace(/[^0-9]/g, '');
        var owner = String(ownerId == null ? '' : ownerId).replace(/[^0-9]/g, '');
        if (lead) refreshActivityCards('[data-call-activity][data-lead-id="' + lead + '"]');
        if (owner) refreshActivityCards('[data-call-activity][data-owner-id="' + owner + '"]');
    }

    // A lead / prospect / deal / property changed: re-fetch every Activity card
    // on screen for that record — page rail and sheet alike — through its own
    // baked-in data-refresh-url. Runs for this tab's own edits too: the save
    // responses re-render only the edited band, so without this the log row a
    // save just wrote (a next-action update, a stage change) stays hidden until
    // a reload. `attr` is 'data-lead-id' (leads and prospects), 'data-deal-id',
    // or 'data-property-id' (the property page and sheet's
    // _property_activity_log.html, whose page and sheet URLs differ by card id).
    //
    // Each card is found again by its refresh URL, never by id: the sheet's URL
    // carries ?sheet=1 and the page's ?sheet=0. The card ids now carry a `-sheet`
    // suffix too, but the URL is what the refresh re-renders from, so matching on
    // it keeps the guard and the swap on one element. When both copies shared the
    // bare 'lead-activity-card' id, the rail card was fetched twice and the
    // sheet's copy never moved. The explicit 0 matters
    // too: refreshFragment sends the address bar as HX-Current-URL, which names
    // the open sheet, so a rail URL without it came back as the sheet variant
    // and its refresh URL then matched the sheet card's.
    //
    // The card re-renders on the All filter with the list clamped, so the
    // viewer's filter pill and "Show more" expansion are carried over from the
    // old node.
    function refreshRecordActivityCards(attr, recordId) {
        var id = String(recordId).replace(/[^0-9]/g, '');
        if (id) refreshActivityCards('[data-log-card][' + attr + '="' + id + '"]');
    }

    // Shared by the record and call refreshes: every card matching `base`,
    // each re-fetched through its own data-refresh-url.
    function refreshActivityCards(base) {
        document.querySelectorAll(base).forEach(function (card) {
            var url = card.getAttribute('data-refresh-url');
            if (!url) return;
            var selector = base + '[data-refresh-url="' + CSS.escape(url) + '"]';
            var resolve = function () { return document.querySelector(selector); };
            deferWhileEditing(selector, function () {
                refreshFragment(resolve, url, restoreActivityCardView);
            }, resolve);
        });
    }

    function restoreActivityCardView(next, prev) {
        if (!next || !prev) return;
        var activePill = prev.querySelector('[data-log-filter].chip-active');
        var filter = activePill ? activePill.getAttribute('data-log-filter') : 'all';
        var prevList = prev.querySelector('[data-clamp-list]');
        var list = next.querySelector('[data-clamp-list]');
        if (list && prevList && prevList.hasAttribute('data-clamp-expanded')) {
            list.setAttribute('data-clamp-expanded', '');
        }
        var pill = filter !== 'all' && next.querySelector('[data-log-filter="' + CSS.escape(filter) + '"]');
        if (pill && window.filterLogEntries) {
            window.filterLogEntries(pill, filter);  // re-applies the clamp itself
        } else if (list && window._cc && window._cc.applyClampList) {
            window._cc.applyClampList(list);
        }
    }

    // Re-fetch a fragment from its own `data-refresh-url`, held while one of
    // its dropdowns is open. A no-op when the element is not on the page.
    function refreshByRefreshUrl(elementId) {
        if (!document.getElementById(elementId)) return;
        deferWhileEditing(elementId, function () {
            var live = document.getElementById(elementId);
            var url = live && live.getAttribute('data-refresh-url');
            if (url) refreshFragment(elementId, url);
        });
    }

    // ── Scope of Work (async generation) ────────────────────────────────────

    // A background SOW generate/regenerate finished (success OR failure). The
    // SOW body (_sow_body.html) is server-rendered six-way keyed on the row's
    // status — confirm panel / generating-spinner+poller / failed+retry /
    // INTAKE questions / finished table (terminal success of the paid takeoff is
    // `intake`, not `draft`) — so we don't build anything in JS: we refetch the
    // body partial and
    // outerHTML-swap the #sow-body wrapper, letting the server decide the state.
    //
    // Fast path vs fallback: THIS WS event is the fast path. The body's own
    // self-terminating poller (hx-trigger="every 15s" inside the generating
    // branch) is the slow fallback that covers a dropped socket — it keeps
    // refetching until a terminal render swaps the poller away. So there is
    // deliberately NO separate JS fallback timer here (unlike skip-trace): the
    // template poller already covers the dropped-socket case for both success
    // and failure, and a duplicate JS timer would be noise (and could fire a
    // premature "timed out" toast while the row is still legitimately in-flight,
    // since the server's staleness cutoff is far longer than a JS timer would be).
    //
    // A SOW is owned by ONE pursuit — a lead or a deal — so the #sow-body
    // wrapper is stamped data-sow-pursuit-kind/-id and we only swap when the
    // finished SOW is the one on screen. data-sow-lead-id is still stamped on
    // the lead branch and still matched here: a payload broadcast by a
    // pre-deploy worker carries lead_id ALONE, and that fallback is what keeps
    // it working. (The reverse — a pre-deploy bundle against this markup —
    // matches on data-sow-lead-id too, which is why the template keeps it.)
    function sowBodyMatches(body, kind, pursuitId) {
        if (!body) return false;
        if (kind) {
            return String(body.getAttribute('data-sow-pursuit-kind')) === String(kind) &&
                String(body.getAttribute('data-sow-pursuit-id')) === String(pursuitId);
        }
        // Old payload, no pursuit keys: it can only ever be a lead.
        return body.hasAttribute('data-sow-lead-id') &&
            String(body.getAttribute('data-sow-lead-id')) === String(pursuitId);
    }

    // The url to re-fetch and the pane to ask for come off the element itself
    // (data-sow-refresh-url / data-sow-tab-target) rather than being rebuilt
    // here, because a deal's scope is a pane on the deal detail page while a
    // lead's is its own url — only the server knows which. The HX-Target header
    // is what makes the view return JUST the body partial (the same header the
    // Scope tab sends), not the full standalone page.
    function refreshSowBody(kind, pursuitId) {
        var body = document.getElementById('sow-body');
        if (!sowBodyMatches(body, kind, pursuitId)) return;
        var url = body.getAttribute('data-sow-refresh-url');
        var target = body.getAttribute('data-sow-tab-target');
        if (!url || !target) return;
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.setRequestHeader('HX-Target', target);
        xhr.setRequestHeader('HX-Current-URL', window.location.href);
        xhr.onload = function () {
            if (xhr.status < 200 || xhr.status >= 300 || !xhr.responseText.trim()) return;
            // Re-check identity: the user may have navigated to another
            // pursuit's SOW while the fetch was in flight — don't paint stale
            // content.
            var live = document.getElementById('sow-body');
            if (!sowBodyMatches(live, kind, pursuitId)) return;
            if (!live.parentNode) return;
            live.outerHTML = xhr.responseText;
            var next = document.getElementById('sow-body');
            if (window.htmx && next) htmx.process(next);
        };
        xhr.send();
    }

    // ── Skip-trace fallback timer (failure-detection layer 3) ──────────────

    // If the skiptrace_complete WS event never arrives (WS connection dropped,
    // worker died before broadcasting), a dropdown rendered in its in-flight
    // state would spin forever. Arm a ~2-minute fallback per in-flight dropdown
    // (keyed by element id, so HTMX re-renders don't stack duplicates). On
    // expiry, re-check the DOM — the node may have been swapped away or the WS
    // event may have landed — and only if it's still in-flight, refresh the
    // dropdown from its GET endpoint (data-skiptrace-refresh-url; same URL the
    // WS payload carries as refresh_url). The server's 120s in-flight auto-stale
    // window has elapsed by then, so the refresh renders the idle state.
    var SKIPTRACE_FALLBACK_MS = 2 * 60 * 1000;
    var skiptraceFallbackTimers = {};

    function clearSkiptraceFallback(elementId) {
        if (skiptraceFallbackTimers[elementId]) {
            clearTimeout(skiptraceFallbackTimers[elementId]);
            delete skiptraceFallbackTimers[elementId];
        }
    }

    function armSkiptraceFallbacks() {
        document.querySelectorAll('[data-skiptrace-in-flight]').forEach(function (el) {
            var elementId = el.id;
            var url = el.getAttribute('data-skiptrace-refresh-url');
            if (!elementId || !url || skiptraceFallbackTimers[elementId]) return;
            skiptraceFallbackTimers[elementId] = setTimeout(function () {
                delete skiptraceFallbackTimers[elementId];
                var current = document.getElementById(elementId);
                if (!current || !current.hasAttribute('data-skiptrace-in-flight')) return;
                refreshFragment(elementId, url);
                if (window.SeeMe && SeeMe.toast) {
                    SeeMe.toast.warning('Skip-trace timed out — try again.');
                }
            }, SKIPTRACE_FALLBACK_MS);
        });
    }

    armSkiptraceFallbacks();
    document.body.addEventListener('htmx:afterSwap', armSkiptraceFallbacks);
    document.body.addEventListener('htmx:oobAfterSwap', armSkiptraceFallbacks);

    // ── Enrichment poll ping (the poll / broadcast guard) ──────────────────

    // `_finalize_enrichment` broadcasts `property_updated` when an async
    // property enrichment lands. The OPEN RECORD resolves through its own
    // self-terminating poll (`_property_enrichment_status.html`), and this
    // broadcast branch deliberately does not fetch the owner card or the
    // property card itself — it only wakes that poll early so the rep does not
    // sit through the remaining delay.
    //
    // `hx-sync="this:drop"` still keeps the BANNER from double-fetching itself: a
    // ping arriving while a tick is in flight is dropped rather than queued, and htmx
    // refuses to issue a request for an element already swapped out of the DOM.
    //
    // What is no longer true is "exactly one thing fetches those regions". The cards
    // are fetched by `refreshPropertyCards` below, which an enrichment reaches twice —
    // once from the socket branch and once from the resolving tick this ping provokes.
    // That is deliberate: the socket path is DOM-independent and works when the banner
    // has been closed, the poll path works when the socket is down, and neither can be
    // dropped without losing a delivery route. The cost is a duplicate GET of an
    // idempotent partial; `deferWhileEditing` collapses them whenever the card is busy.
    function pingEnrichmentPoll(propertyId) {
        if (!window.htmx) return;
        var selector = '[data-enrichment-property-id="' + propertyId + '"]';
        document.querySelectorAll(selector).forEach(function (el) {
            htmx.trigger(el, 'acqsEnrichmentPing');
        });
    }

    // ── Property card refresh ──────────────────────────────────────────────

    // Every card that can be re-rendered live carries its own `data-refresh-url`,
    // baked at render time where its surface and its presentation flags are
    // known, plus the `data-property-id` it belongs to.
    //
    // This is what replaced the server-side region list. The surface context
    // used to travel the other way — the CALLER built a `?regions=` list and the
    // server rendered those regions back OOB — and only the enrichment banner
    // could build that string, so every other trigger started the work and then
    // swapped nothing on screen.
    //
    // Three things call this and nothing else does: the socket, on a
    // `property_updated` that names enrichment as its reason; the banner poll's
    // resolving tick; and the address-repair round trip. The last two arrive as
    // an `acqsRefreshPropertyCards` HX-Trigger.
    //
    // `deferWhileEditing` is the same guard the list rows use, and it is doing
    // two jobs here: a card holding a focused input or an open dropdown keeps
    // its state until the rep is done with it, and a burst of broadcasts on one
    // card collapses to a single swap of the newest markup. The poll got that
    // second half from `hx-sync="this:drop"` on its own element.
    function refreshPropertyCards(propertyId) {
        var id = String(propertyId).replace(/[^0-9]/g, '');
        if (!id) return;
        // The Activity log carries data-property-id too, but refreshes on every
        // property_updated through refreshRecordActivityCards, which also keeps
        // its filter pill and "Show more".
        document.querySelectorAll('[data-refresh-url][data-property-id="' + id + '"]:not([data-log-card])').forEach(function (el) {
            // refreshByRefreshUrl re-reads the URL rather than closing over it:
            // a deferred swap can wait out several seconds of typing, and the
            // card may have been re-rendered onto a different surface meanwhile.
            if (el.id) refreshByRefreshUrl(el.id);
        });
    }

    // The snapshot card is the exception to the enrichment-only rule above: it is
    // read-only and has no tabs, so a refresh can neither reset a tab nor
    // double-swap an inline edit. The lead page shows it instead of the details
    // card, and property edits happen in the property sheet opened over it, so it
    // refreshes on every `property_updated`. Enrichment goes through
    // `acqsRefreshPropertyCards`, which already covers it.
    function refreshSnapshotCards(propertyId) {
        var id = String(propertyId).replace(/[^0-9]/g, '');
        if (!id) return;
        surfaceCards('property-snapshot').forEach(function (el) {
            if (el.getAttribute('data-property-id') !== id) return;
            var url = el.getAttribute('data-refresh-url');
            if (url) refreshFragment(el.id, url);
        });
    }

    // `HX-Trigger: {"acqsRefreshPropertyCards": {"property_id": N}}`, sent by a
    // round trip that already knows the enrichment resolved.
    //
    // One enrichment fires this event TWICE, and the socket is what causes both.
    // The `property_updated` handler calls `pingEnrichmentPoll` and then triggers
    // this event; the ping makes the banner re-fetch `property_enrichment_status`
    // immediately, that fetch finds the row resolved, and the response's own
    // `HX-Trigger` sends the event a second time one round trip later. Measured at
    // 88ms apart on bay d. Both arrive here, so both are collapsed here.
    //
    // The window only has to outlast that round trip, so it is set well above it
    // rather than tuned to it. Nothing legitimately needs two refreshes of one
    // property inside a second and a half: the enrich cooldown is an hour, and the
    // other sender — the address-repair round trip — is a human action.
    //
    // A timestamp rather than an in-flight flag on purpose. `refreshFragment` only
    // calls its callback on a 2xx WITH a non-empty body, and `property_card_partial`
    // answers 204 by design when the card has no property to render, so an
    // in-flight flag would never be cleared for that card and would silently kill
    // its live refresh until a reload.
    var REFRESH_COALESCE_MS = 1500;
    var lastCardRefresh = Object.create(null);
    document.body.addEventListener('acqsRefreshPropertyCards', function (event) {
        var detail = event.detail || {};
        var propertyId = detail.property_id;
        if (!propertyId) return;
        var now = Date.now();
        if (now - (lastCardRefresh[propertyId] || 0) < REFRESH_COALESCE_MS) return;
        lastCardRefresh[propertyId] = now;
        refreshPropertyCards(propertyId);
    });

    // `buyer_update_field` sends `buyerRenamed` after a buyer name edit.  The edited title
    // already shows the name; this repaints the buyer page breadcrumb and tab title, and the
    // other title when the buyer's page and sheet are both open.  The tab title format is
    // `buyer_detail.html`'s page_title inside `acqs/_base.html`'s title block.
    document.body.addEventListener('buyerRenamed', function (event) {
        var detail = event.detail || {};
        if (!detail.id || !detail.name) return;
        document.querySelectorAll('[data-buyer-name="' + detail.id + '"]').forEach(function (el) {
            if (el.tagName === 'TEXTAREA') {
                el.value = detail.name;
                el.defaultValue = detail.name;
            } else {
                el.textContent = detail.name;
            }
            if (el.hasAttribute('data-buyer-crumb')) document.title = 'Buyer: ' + detail.name + ' | RealFlips';
        });
    });

    // ── Sheet refresh helper ───────────────────────────────────────────────

    function sheetMatches(prefix, id) {
        var sheetParam = new URLSearchParams(window.location.search).get('sheet') || '';
        return sheetParam.indexOf(prefix + id + '/') !== -1;
    }

    // `recordKind` is 'lead', 'prospect', 'deal', 'offer' or 'listing' — the banner is record-kind
    // keyed rather than pipeline-keyed because AcqDeal has no pipeline field.
    // Every match: a listing sheet can sit over the same listing's page, and
    // both are stale.
    function showStaleBanner(recordKind, recordId) {
        document.querySelectorAll('[data-acqs-stale-banner][data-acqs-record-id="' + recordId + '"][data-acqs-record-kind="' + recordKind + '"]').forEach(function (banner) {
            banner.hidden = false;
        });
    }

    document.addEventListener('click', function (event) {
        var dismiss = event.target.closest('[data-acqs-stale-dismiss]');
        if (dismiss) {
            var banner = dismiss.closest('[data-acqs-stale-banner]');
            if (banner) banner.hidden = true;
            return;
        }
        var refresh = event.target.closest('[data-acqs-stale-refresh]');
        if (!refresh) return;
        // Refresh the surface the banner itself is on, not "whatever sheet
        // happens to be open": deal_detail.html is a full page from which
        // appointment/property sheets are routinely opened, and a document-wide
        // `.sheet.show` test would refresh that sheet and leave the stale page
        // underneath exactly as it was.
        var staleBanner = refresh.closest('[data-acqs-stale-banner]');
        var bannerSheet = staleBanner && staleBanner.closest('.sheet');
        if (bannerSheet && window.rfSheet && window.rfSheet.refresh) {
            window.rfSheet.refresh();
        } else {
            window.location.reload();
        }
    });

    // ── WebSocket connection ───────────────────────────────────────────────

    var socket = null;
    var reconnectTimer = null;
    var reconnectAttempts = 0;
    var SLOW_RECONNECT_MS = 60000;
    // Set immediately before `checkConnection` closes the socket ON PURPOSE, and
    // cleared by the next open. It gates the catch-up below. Not derived from
    // `onclose`: that handler returns early once the user is off /acqs/, which is
    // exactly the deliberate case, so a flag set there would never be cleared.
    var deliberateClose = false;
    // The first connect needs no catch-up — the page it would refresh was
    // rendered by the server moments earlier.
    var hasConnected = false;

    // The socket dropped and came back, and nothing queued what it missed while
    // it was down: an enrichment that landed in that window told this browser
    // nothing. Re-fetch the live cards once, here, in the socket layer. This is
    // the alternative to every feature growing its own timer for a disconnect it
    // did not cause. Scoped to property cards because those are the ones that
    // declare a `data-property-id` (the property Activity log declares one too,
    // and `refreshPropertyCards` skips it); the activity cards' `data-refresh-url` ids
    // are not surface-unique yet, so a blanket sweep would swap the wrong one.
    function refreshLivePropertyCards() {
        if (!window.htmx) return;
        var seen = Object.create(null);
        document.querySelectorAll('[data-refresh-url][data-property-id]').forEach(function (el) {
            var id = el.getAttribute('data-property-id');
            if (!id || seen[id]) return;
            seen[id] = true;
            // Fire the event rather than calling `refreshPropertyCards` directly, for
            // the same reason the socket handler does: the pipeline badges have no
            // `data-refresh-url` of their own and hear about a refresh only through
            // `acqsRefreshPropertyCards from:body`. Calling the function here walked
            // straight past them, so a reconnect re-rendered every card and left the
            // AVM and equity chips showing pre-enrichment numbers — the exact
            // staleness the socket handler switched to the event to avoid. It also
            // puts this path behind the coalescing guard on the listener.
            htmx.trigger(document.body, 'acqsRefreshPropertyCards', {property_id: id});
        });
    }

    // The list half of the reconnect catch-up: every acqs list renders its rows
    // in #list-results-wrapper, and a missed create, edit or delete can change
    // any row, the count or the tab totals, so refetch the whole page's results.
    // #tabs-bar rides along; #filter-bar does not, because it holds the search
    // input and swapping it would discard what the rep is typing. Held while the
    // rep is typing in a row or has a row dropdown open, like a row swap.
    // Only on acqs pages: a pm_crm or marketing list that holds the socket open
    // for a lead sheet never hears acqs events, so it has nothing to catch up on.
    function refreshListAfterReconnect() {
        if (!window.htmx || !isOnAcqsPage() || !document.getElementById('list-results-wrapper')) return;
        deferWhileEditing('list-results-wrapper', function () {
            var wrapper = document.getElementById('list-results-wrapper');
            if (!wrapper) return;
            htmx.ajax('GET', window.location.href, {
                source: wrapper,
                target: wrapper,
                select: '#list-results-wrapper',
                selectOOB: '#tabs-bar',
                swap: 'outerHTML',
                push: 'false',
            });
        });
    }

    function connect() {
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }

        var protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        socket = new WebSocket(protocol + '//' + location.host + '/ws/acqs/');

        socket.onopen = function () {
            var missedEvents = hasConnected && !deliberateClose;
            reconnectAttempts = 0;
            hasConnected = true;
            deliberateClose = false;
            if (missedEvents) {
                refreshLivePropertyCards();
                refreshLiveIntakeProgressCards();
                refreshListAfterReconnect();
                refreshAllTargetEmails();
            }
        };

        socket.onmessage = function (e) {
            var data = JSON.parse(e.data);

            // NOTE: never call rfSheet.refresh() on *_updated events. Every
            // field edit / enrichment / image change broadcasts property_updated,
            // and a full sheet refresh blows away the targeted partial swaps the
            // initiating tab just received (and clobbers in-progress edits in
            // any other open tab). Cross-tab sheet sync would need an event
            // model that names the changed region, not a blanket refresh.
            if (data.type === 'property_updated') {
                if (isOnPropertyList()) refreshPropertyRow(data.property_id);
                refreshRowsForProperty(data.property_id);
                pingEnrichmentPoll(data.property_id);
                // The cards refresh on an enrichment write and on nothing else.
                // `property_updated` is broadcast from ~30 sites — every inline
                // field edit, address change, file upload and skip-trace write —
                // and re-fetching the cards on those resets the details card to
                // its Details tab under a rep working in Data or Comps, and
                // double-swaps the response to the rep's own inline edit. That
                // is the regression `property_enrichment_status`'s `polling=1`
                // gate already exists to stop; `reason` is the same
                // discriminator reaching the socket. A message published by a
                // pre-deploy worker carries no `reason` and so is treated as
                // one of the ~30.
                // Fire the EVENT rather than calling the function: the pipeline badges
                // listen for `acqsRefreshPropertyCards from:body` and have no
                // `data-refresh-url` of their own, so calling `refreshPropertyCards`
                // directly left the AVM and equity chips stale on the socket path and
                // after a reconnect. It also strands them entirely when a rep enriches
                // inside a sheet and closes it before the lookup lands — sheets.js
                // removes the banner's DOM, so the poll's resolving tick, the only other
                // source of this event, never fires. One entry point, everything hears it.
                // The Activity log is the other exception: it has no tabs and no
                // inline edits, and a save's log row must show without a reload.
                refreshRecordActivityCards('data-property-id', data.property_id);
                if (data.reason === 'enrichment' && window.htmx) {
                    htmx.trigger(document.body, 'acqsRefreshPropertyCards', {
                        property_id: data.property_id,
                    });
                } else {
                    refreshSnapshotCards(data.property_id);
                }

            } else if (data.type === 'property_created') {
                if (isOnPropertyList()) upsertPropertyRow(data.property_id);

            } else if (data.type === 'property_deleted') {
                if (isOnPropertyList()) removePropertyRow(data.property_id);
                if (sheetMatches('/properties/', data.property_id) && window.rfSheet) {
                    window.rfSheet.close();
                }

            } else if (data.type === 'owner_updated') {
                if (isOnOwnerList()) refreshOwnerRow(data.owner_id);
                if (isOnBuyerList()) refreshBuyerRowsForOwner(data.owner_id);
                refreshCallActivityCard(null, data.owner_id);

            } else if (data.type === 'buyer_updated') {
                if (isOnBuyerList()) refreshBuyerRow(data.buyer_id);
                refreshBuyerSections(data.buyer_id, data.links);

            } else if (data.type === 'buyer_deleted') {
                if (isOnBuyerList()) removeRow(document.getElementById('buyer-' + data.buyer_id));
                if (sheetMatches('/buyers/', data.buyer_id) && window.rfSheet) {
                    window.rfSheet.close();
                }

            } else if (data.type === 'owner_created') {
                if (isOnOwnerList()) upsertOwnerRow(data.owner_id);

            } else if (data.type === 'owner_deleted') {
                if (isOnOwnerList()) removeOwnerRow(data.owner_id);
                if (isOnBuyerList()) refreshBuyerRowsForOwner(data.owner_id);
                if (sheetMatches('/owners/', data.owner_id) && window.rfSheet) {
                    window.rfSheet.close();
                }

            } else if (data.type === 'lead_updated') {
                refreshRecordActivityCards('data-lead-id', data.lead_id);
                // The page rail's Appointments list (_pursuit_appointments.html).
                // An appointment-sheet save OOB-swaps it; every other write that
                // changes the pursuit's appointments — a terminal stage or status
                // cancelling them through close_pursuit_appointments, a discard,
                // an assignment or listing close — only broadcasts lead_updated /
                // deal_updated, so the list reloads on those.
                refreshByRefreshUrl('lead-appointments-' + data.lead_id);
                // The Appts rail button's scheduled count sits outside that list.
                refreshByRefreshUrl('lead-appointments-badge-' + data.lead_id);
                // A lead sheet's Appointments chip (_pursuit_appointments_chip.html), its rail stand-in.
                refreshByRefreshUrl('lead-appointments-chip-' + data.lead_id);
                // The state banner and the mail stop chip, page and sheet. A write
                // from elsewhere (an inbound text reactivating a Discarded lead)
                // changes the status or snooze they show without an OOB swap here.
                ['acq-state-banner-', 'pursuit-mail-stop-'].forEach(function (prefix) {
                    refreshByRefreshUrl(prefix + data.lead_id);
                    refreshByRefreshUrl(prefix + data.lead_id + '-sheet');
                });
                if (data.pipeline === 'prospect') {
                    if (isOnProspectList()) refreshProspectRow(data.lead_id);
                    if (isOnProspectList()) refreshProspectTabs();
                    if (isOnProspectList()) refreshProspectQuickFilters();
                    if (data.tab_id && data.tab_id !== acqsTabId && (sheetMatches('/prospects/', data.lead_id) || isOnProspectDetail(data.lead_id))) {
                        showStaleBanner('prospect', data.lead_id);
                    }
                } else if (data.pipeline === 'lead') {
                    if (isOnLeadList() && !refreshExplicitlySortedLeadList()) {
                        refreshLeadRow(data.lead_id);
                        refreshLeadTabs();
                        refreshLeadQuickFilters();
                    }
                    if (data.tab_id && data.tab_id !== acqsTabId && (sheetMatches('/leads/', data.lead_id) || isOnLeadDetail(data.lead_id))) {
                        showStaleBanner('lead', data.lead_id);
                    }
                } else {
                    refreshVisibleLeadOrProspectRow(data.lead_id);
                }

            } else if (data.type === 'lead_created') {
                if (data.pipeline === 'prospect') {
                    if (isOnProspectList()) upsertProspectRow(data.lead_id);
                    if (isOnProspectList()) refreshProspectTabs();
                    if (isOnProspectList()) refreshProspectQuickFilters();
                } else {
                    if (isOnLeadList() && !refreshExplicitlySortedLeadList()) {
                        upsertLeadRow(data.lead_id);
                        refreshLeadTabs();
                        refreshLeadQuickFilters();
                    }
                }

            } else if (data.type === 'lead_deleted') {
                if (data.pipeline === 'prospect') {
                    if (isOnProspectList()) removeProspectRow(data.lead_id);
                    if (isOnProspectList()) refreshProspectTabs();
                    if (isOnProspectList()) refreshProspectQuickFilters();
                    if (sheetMatches('/prospects/', data.lead_id) && window.rfSheet) {
                        if (data.converted_to_lead_url) {
                            window.rfSheet.open(data.converted_to_lead_url);
                        } else {
                            window.rfSheet.close();
                        }
                    }
                } else {
                    if (isOnLeadList()) removeLeadRow(data.lead_id);
                    if (isOnLeadList()) refreshLeadTabs();
                    if (isOnLeadList()) refreshLeadQuickFilters();
                    if (sheetMatches('/leads/', data.lead_id) && window.rfSheet) {
                        window.rfSheet.close();
                    }
                }

            } else if (data.type === 'deal_updated') {
                refreshRecordActivityCards('data-deal-id', data.deal_id);
                // The deal Overview's facts row (_deal_overview_facts.html) shows
                // the rail's money, COE and possession figures. A save from the
                // row itself carries the row back as an OOB; a save from the rail
                // (or another tab) re-renders only the summary panel, so the row
                // reloads on every deal_updated.
                refreshByRefreshUrl('deal-overview-facts-' + data.deal_id);
                // The rail's Appointments list; see lead_updated above.
                refreshByRefreshUrl('deal-appointments-' + data.deal_id);
                refreshByRefreshUrl('deal-appointments-badge-' + data.deal_id);
                refreshByRefreshUrl('deal-appointments-chip-' + data.deal_id);
                if (isOnDealList()) {
                    upsertDealRow(data.deal_id);
                    refreshDealTabs();
                }
                refreshPartnerListDebounced();
                if (data.tab_id && data.tab_id !== acqsTabId && (sheetMatches('/deals/', data.deal_id) || isOnDealDetail(data.deal_id))) {
                    showStaleBanner('deal', data.deal_id);
                }

            } else if (data.type === 'offer_updated') {
                // A nudge only: the offer page has no in-place refresh, so the
                // banner's Refresh reloads the page (the banner is not in a sheet).
                if (data.tab_id && data.tab_id !== acqsTabId && isOnOfferDetail(data.offer_id)) {
                    showStaleBanner('offer', data.offer_id);
                }

            } else if (data.type === 'deal_created') {
                if (isOnDealList()) {
                    upsertDealRow(data.deal_id);
                    refreshDealTabs();
                }
                refreshPartnerListDebounced();

            } else if (data.type === 'assignment_updated') {
                if (isOnAssignmentList()) {
                    upsertAssignmentRow(data.assignment_id);
                    refreshAssignmentTabs();
                }

            } else if (data.type === 'listing_updated') {
                // Activity cards and next-action summaries re-fetch in place on
                // every change, this tab's included, as on lead_updated: a note
                // deleted from the sheet over its own page removes only the
                // sheet's copy. Any other change from another tab shows the
                // banner rather than re-rendering the page or sheet under the
                // viewer, which would throw away an open terms editor. This
                // tab's own change re-renders from its response
                // (listingLifecycleChanged, the terms card's swap).
                refreshRecordActivityCards('data-listing-id', data.listing_id);
                refreshListingNextActionSummaries(data.listing_id);
                if (!data.in_place && data.tab_id && data.tab_id !== acqsTabId && (sheetMatches('/listings/', data.listing_id) || isOnListingDetail(data.listing_id))) {
                    showStaleBanner('listing', data.listing_id);
                }
                if (isOnListingList()) {
                    upsertListingRow(data.listing_id);
                    refreshListingTabs();
                }

            } else if (data.type === 'lead_search_indexed') {
                refreshSearchFilteredListAfterIndex(data.pipeline);

            } else if (data.type === 'intake_resolution_progress') {
                refreshLiveIntakeProgressCards(data.request_id, data.lead_id, data.deal_id);

            } else if (data.type === 'message_received') {
                var leads = data.leads || (data.lead_ids || []).map(function (leadId) {
                    return { id: leadId, pipeline: 'lead' };
                });
                // An open conversation of the owner's is refreshed below, so a lead timeline among them is
                // refreshed only there; the lead-timeline refresh covers the rest (a lead with no owner).
                var ownerConversations = '[data-acqs-conversation][data-owner-id="' + data.owner_id + '"]';
                leads.forEach(function (lead) {
                    refreshSellerBotForLead(lead.id);
                    var inOpenConversation = data.owner_id && document.querySelector(
                        ownerConversations + '[data-lead-timeline][data-lead-id="' + lead.id + '"]');
                    if (!inOpenConversation) refreshLeadTimeline(lead.id);
                });
                // Buyers and partners come in an event of their own (no owner, no leads).
                // The badge recounts after the open conversation's refresh, so a visible
                // thread is already marked read.
                [['buyer', data.buyer_ids], ['partner', data.partner_ids]].forEach(function (pair) {
                    var kind = pair[0];
                    (pair[1] || []).forEach(function (targetId) {
                        var id = String(targetId).replace(/[^0-9]/g, '');
                        var refreshBadge = function () { refreshTargetSmsUnread(kind, id); };
                        if (!refreshOpenConversation('[data-acqs-conversation][data-' + kind + '-id="' + id + '"]', refreshBadge)) {
                            refreshBadge();
                        }
                    });
                });
                var refreshCardsAndRows = function () {
                    leads.forEach(function (lead) {
                        refreshRailSmsUnread(lead.id);
                        if (lead.pipeline === 'prospect') {
                            if (isOnProspectList()) refreshProspectRow(lead.id);
                        } else if (isOnLeadList()) {
                            refreshLeadRow(lead.id);
                        }
                    });
                };
                if (data.owner_id || leads.length) {
                    if (!refreshOpenConversation(ownerConversations, refreshCardsAndRows)) {
                        refreshCardsAndRows();
                    }
                }

            } else if (data.type === 'email_synced') {
                // A Gmail sync or a send stored new mail for these leads', buyers' and
                // partners' contacts, or an open cleared one of their threads' unread count.
                (data.lead_ids || []).forEach(function (leadId) {
                    refreshTargetEmails('lead', leadId);
                    refreshSellerBotForLead(leadId);
                });
                (data.buyer_ids || []).forEach(function (buyerId) {
                    refreshTargetEmails('buyer', buyerId);
                });
                (data.partner_ids || []).forEach(function (partnerId) {
                    refreshTargetEmails('partner', partnerId);
                });

            } else if (data.type === 'seller_bot_changed') {
                refreshSellerBotSoon();
                refreshBotDrafts(data.lead_id);
                refreshBotComposer(data.lead_id);
                if (data.lead_id) {
                    refreshLeadTimeline(data.lead_id);
                    refreshBotLeadChrome(data.lead_id);
                }
                // What came of a rep's instruction, on a sheet open on this lead.
                if (data.notice && window.SeeMe && SeeMe.toast
                        && document.querySelector('[data-bot-drafts][data-lead-id="' + data.lead_id + '"]')) {
                    (SeeMe.toast[data.notice_type] || SeeMe.toast.info)(data.notice);
                }

            } else if (data.type === 'signal_updated') {
                if (isOnSignalQueue()) refreshSignalRow(data.signal_id);

            } else if (data.type === 'signal_deleted') {
                if (isOnSignalQueue()) removeSignalRow(data.signal_id);
                if (sheetMatches('/signals/', data.signal_id) && window.rfSheet) {
                    window.rfSheet.close();
                }

            } else if (data.type === 'proposal_updated') {
                if (isOnLeadDetail(data.lead_id)) {
                    refreshProposalsCard(data.lead_id);
                }

            } else if (data.type === 'call_answered') {
                // The callee leg answered — let the dialer dock flip to its
                // in-call timer IF this is the dock's active call.
                if (window.Dialer && window.Dialer.onAnswered) {
                    window.Dialer.onAnswered(data.attempt_id);
                }

            } else if (data.type === 'call_logged') {
                // A finished call wrote its completion log — live-refresh the
                // linked record's Activity card if it's on screen.
                refreshCallActivityCard(data.lead_id, data.owner_id);

            } else if (data.type === 'property_images_updated') {
                refreshPropertyImagesCardDebounced(data.property_id);

            } else if (data.type === 'sow_generated') {
                // Background SOW generation finished (success or failure) —
                // refetch the body partial; the server renders the finished
                // table or the failed+retry state, keyed on the row's status.
                // No pursuit_kind = payload from a pre-deploy worker; fall back
                // to lead_id, which is what that build sent.
                refreshSowBody(data.pursuit_kind, data.pursuit_kind ? data.pursuit_id : data.lead_id);

            } else if (data.type === 'skiptrace_complete') {
                // Async skip-trace finished — swap the pre-rendered dropdown
                // back in (clears the in-flight spinner), fire the server's
                // extra triggers so dependent cards (contact phones, owner
                // card, pipeline badges) refresh via their existing from:body
                // listeners, and show the toast (the consumer already strips
                // it for users who didn't trigger the skip-trace).
                var payload = data.payload || {};
                // Only the dropdown for THIS property is touched: another open
                // sheet's dropdown shares the `<target_id>-skiptrace-dropdown` id.
                var dropdown = payload.target_id
                    ? document.getElementById(payload.target_id + '-skiptrace-dropdown')
                    : null;
                if (dropdown && dropdown.getAttribute('data-skiptrace-prop') !== String(data.property_id)) {
                    dropdown = null;
                }
                if (dropdown) clearSkiptraceFallback(dropdown.id);
                if (dropdown && payload.dropdown_html) {
                    var dropdownParent = dropdown.parentNode;
                    if (dropdownParent) {
                        dropdown.outerHTML = payload.dropdown_html;
                        if (window.htmx) htmx.process(dropdownParent);
                    }
                }
                if (window.htmx && payload.extra_triggers) {
                    Object.keys(payload.extra_triggers).forEach(function (name) {
                        htmx.trigger(document.body, name);
                    });
                }
                if (payload.toast_message && window.SeeMe && SeeMe.toast) {
                    (SeeMe.toast[payload.toast_type] || SeeMe.toast.info)(payload.toast_message);
                }
            }
        };

        socket.onclose = function (e) {
            socket = null;
            if (e.code === 4401) {
                window.location.href = '/login/?next=' + encodeURIComponent(window.location.pathname);
                return;
            }
            // 4403: the server refused this user (no client or no acqs permission).
            // Retrying cannot change that.
            if (e.code === 4403 || !needsSocket()) return;
            // Back off to 30s over the first ten attempts, then keep trying once a
            // minute while the tab is visible. Giving up left a tab that slept
            // through a deploy deaf for the rest of its life. A hidden tab stops
            // here; the `visibilitychange` listener below reconnects it when the
            // rep comes back, and `online` skips the wait when the wifi returns.
            if (reconnectAttempts >= 10 && document.hidden) return;
            var delay = reconnectAttempts < 10
                ? Math.min(2000 * Math.pow(1.5, reconnectAttempts), 30000)
                : SLOW_RECONNECT_MS;
            reconnectAttempts++;
            reconnectTimer = setTimeout(connect, delay);
        };
    }

    if (needsSocket()) connect();

    // SPA navigation and htmx swaps — connect/disconnect as the page gains or
    // loses a reason to listen (an /acqs/ or offer page path, or a skip trace dropdown).
    function checkConnection() {
        if (needsSocket() && !socket) {
            reconnectAttempts = 0;
            connect();
        } else if (!needsSocket() && socket) {
            deliberateClose = true;
            socket.close();
            socket = null;
            if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
        }
    }
    document.body.addEventListener('htmx:pushedIntoHistory', checkConnection);
    window.addEventListener('popstate', checkConnection);
    document.body.addEventListener('htmx:afterSettle', checkConnection);
    // Sheets opened by script fire sheet:ready without an htmx settle.
    document.addEventListener('sheet:ready', checkConnection);
    // A waking laptop or returning wifi reconnects now instead of at the next
    // slow retry. checkConnection does nothing while a socket is open or opening.
    window.addEventListener('online', checkConnection);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') checkConnection();
    });

})();
